import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import { createClient } from '@supabase/supabase-js';
import { toast } from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  Search,
  Settings,
  Plus,
  Paperclip,
  Send,
  Smile,
  User,
  Users,
  MoreVertical,
  Phone,
  Video,
  Star,
  Image,
  Mic,
  FileText,
  ChevronLeft,
  Circle,
  ArrowDown,
  Hash,
  PanelRight,
  PanelLeft,
  Filter,
  Bell,
  MessageCircle,
  UserPlus,
  Check,
  ChevronRight,
  Archive,
  Sun,
  Moon,
  Laptop,
  Info,
  PenSquare,
  LogOut,
  BellOff,
  UserX,
  Shield,
  Mail,
  Briefcase,
  Camera,
  UserMinus,
  UserCog,
  Upload,
  RefreshCw,
  X,
} from 'lucide-react';

// Supabase client
const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY
);

// Create notification sound 
const notificationSound = new Audio('https://assets.mixkit.co/active_storage/sfx/2358/2358-preview.mp3');

// Bildirim ses ayarlarını localStorage'dan al
const getNotificationSoundSettings = () => {
  try {
    const settings = localStorage.getItem('notificationSettings');
    if (settings) {
      return JSON.parse(settings);
    }
    return { sound: true }; // Varsayılan olarak ses açık
  } catch {
    return { sound: true };
  }
};

// Play notification sound
const playNotificationSound = (senderId) => {
  try {
    // Engellenen kullanıcıdan geliyorsa ses çalma
    const blockedUsersList = JSON.parse(localStorage.getItem('blockedUsers') || '[]');
    if (blockedUsersList.includes(senderId)) {
      return;
    }
    
    const settings = getNotificationSoundSettings();
    if (settings.sound) {
      notificationSound.currentTime = 0;
      notificationSound.play();
    }
  } catch (error) {
    console.error('Bildirim sesi çalınamadı:', error);
  }
};

// Örnek veriler kaldırıldı

const Chat = () => {
  const { isDark } = useTheme();
  const [activeChat, setActiveChat] = useState(null);
  const [showChats, setShowChats] = useState(true);
  const [message, setMessage] = useState('');
  const [filter, setFilter] = useState('all'); // 'all', 'direct', 'groups'
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [typingTimeout, setTypingTimeout] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const messageEndRef = useRef(null);
  const messageContainerRef = useRef(null); // Mesaj container referansı
  const textareaRef = useRef(null); // Textarea referansı
  const [showScrollBottom, setShowScrollBottom] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState([]);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const fileInputRef = useRef(null); // Dosya giriş referansı
  
  // Chat Header dropdown state
  const [showHeaderDropdown, setShowHeaderDropdown] = useState(false);
  const headerDropdownRef = useRef(null);
  
  // Veritabanı verilerini tutan state'ler
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]); // Tüm kullanıcıları tutacak state
  
  // Yeni eklenen state değişkenleri
  const [conversations, setConversations] = useState([]);
  const [conversationsLoading, setConversationsLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [messageOffset, setMessageOffset] = useState(0);

  // Grup oluşturma ile ilgili state'ler
  const [chatModalTab, setChatModalTab] = useState('direct'); // 'direct' veya 'group'
  const [selectedGroupMembers, setSelectedGroupMembers] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [groupCreating, setGroupCreating] = useState(false);
  const [groupPhoto, setGroupPhoto] = useState(null);
  const [groupPhotoPreview, setGroupPhotoPreview] = useState(null);
  const [showManageMembersModal, setShowManageMembersModal] = useState(false);

  // Yeni bir state değişkeni oluştur
  const [highlightedChat, setHighlightedChat] = useState(null);
  
  // Ayarlar modalı için state
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  
  // Chat bilgi modülü için state
  const [showChatInfo, setShowChatInfo] = useState(false);
  
  // Grup düzenleme için state'ler
  const [isEditingGroupName, setIsEditingGroupName] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [potentialMembers, setPotentialMembers] = useState([]);
  const [selectedNewMembers, setSelectedNewMembers] = useState([]);
  
  // Grup yönetici özellikleri için state'ler
  const [isEditingGroupDesc, setIsEditingGroupDesc] = useState(false);
  const [newGroupDesc, setNewGroupDesc] = useState('');
  const [isChangingGroupPhoto, setIsChangingGroupPhoto] = useState(false);
  // Kişi ayarları için state'ler
  const [mutedChats, setMutedChats] = useState(() => {
    const saved = localStorage.getItem('mutedChats');
    return saved ? JSON.parse(saved) : [];
  });
  const [blockedUsers, setBlockedUsers] = useState(() => {
    const saved = localStorage.getItem('blockedUsers');
    return saved ? JSON.parse(saved) : [];
  });
  const [archivedChats, setArchivedChats] = useState(() => {
    const saved = localStorage.getItem('archivedChats');
    return saved ? JSON.parse(saved) : [];
  });
  const [showSearchChatModal, setShowSearchChatModal] = useState(false);
  const [chatSearchQuery, setChatSearchQuery] = useState('');
  const [chatSearchResults, setChatSearchResults] = useState([]);
  const [showBlockedUsersModal, setShowBlockedUsersModal] = useState(false);
  const [showArchivedChatsModal, setShowArchivedChatsModal] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchMode, setSearchMode] = useState(false);
  const [searchResultsVisible, setSearchResultsVisible] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null); // Seçilen dosyayı tutacak state
  const [filePreview, setFilePreview] = useState(null); // Dosya önizlemesi için state

  const removeOldFile = async (publicUrl) => {
    if (!publicUrl) return;
  
    try {
      // Örnek URL:
      // https://xxxx.supabase.co/storage/v1/object/public/chat-avatars/group_photos/myimage.png
      // chat-avatars/ konumundan sonraki kısım: group_photos/myimage.png
  
      // 1) "chat-avatars/" sonrasını ayıklayalım:
      const SPLIT_KEY = '/chat-avatars/';
      const parts = publicUrl.split(SPLIT_KEY);
      if (parts.length < 2) {
        // URL beklenen formatta değilse çık
        console.warn('Public URL beklenen formatta değil:', publicUrl);
        return;
      }
  
      // 2) Asıl dosya yolunu bulalım (ör: group_photos/myimage.png):
      const filePath = parts[1]; // chat-avatars/ kısmından sonraki kısım
  
      // 3) Dosyayı sil
      //console.log('Eski dosya siliniyor:', filePath);
      const { error } = await supabase
        .storage
        .from('chat-avatars')
        .remove([filePath]);
  
      if (error) {
        console.error('Eski dosya silinemedi:', error);
      } else {
        //console.log('Eski fotoğraf başarıyla silindi:', filePath);
      }
    } catch (err) {
      console.error('Eski fotoğraf silme sırasında hata:', err);
    }
  };

  // Kullanıcının çevrimiçi durumunu güncelle
  const updateOnlineStatus = async (userId, isOnline) => {
    try {
      if (!userId) {
        console.warn('Çevrimiçi durumu güncellenemiyor: Kullanıcı ID eksik');
        return;
      }
      
      // console.log(`Kullanıcı ${userId} için çevrimiçi durumu güncelleniyor: ${isOnline}`);
      
      // Direkt veritabanını güncelle
      const { error } = await supabase
        .from('user_profiles')
        .update({ online_status: isOnline })
        .eq('id', userId);
      
      if (error) {
        console.error('Çevrimiçi durumu güncellenirken hata:', error);
      } else {
        // console.log(`Çevrimiçi durumu başarıyla güncellendi: ${isOnline ? 'çevrimiçi' : 'çevrimdışı'}`);
      }
    } catch (error) {
      console.error('Çevrimiçi durumu güncellenirken bir hata oluştu:', error);
    }
  };

  // Mevcut kullanıcıyı al
  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          throw error;
        }
        
        if (session?.user) {
          // Kullanıcı profil bilgilerini çek
          const { data: userProfile, error: profileError } = await supabase
            .from('user_profiles')
            .select('*')
            .eq('id', session.user.id)
            .single();
            
          if (profileError) {
            throw profileError;
          }
          
          setCurrentUser(userProfile);
          
          // Only update online status if we have a valid user profile
          if (userProfile && userProfile.id) {
            updateOnlineStatus(userProfile.id, true);
            
            // Engellenen kullanıcıları veritabanından yükle
            loadBlockedUsers();
          }
        }
      } catch (error) {
        console.error('Oturum bilgisi alınamadı:', error);
        toast.error('Kullanıcı bilgileri yüklenemedi');
      } finally {
        // Mevcut kullanıcı çekilemese bile tüm kullanıcıları çekebilmek için
        fetchAllUsers();
      }
    };
    
    getCurrentUser();
  }, []);  // Sadece mount olduğunda çalıştır

  // Kullanıcının çevrimiçi durumunu düzenli aralıklarla güncelleyen ping mekanizması
  useEffect(() => {
    // Kullanıcı varsa her 5 dakikada bir online durumunu yenileyen bir interval oluştur
    if (!currentUser?.id) return;
    
    // İlk olarak online durumunu ayarla
    updateOnlineStatus(currentUser.id, true);
    
    // Her 5 dakikada bir ping gönder
    const pingInterval = setInterval(() => {
      updateOnlineStatus(currentUser.id, true);
      // console.log('Çevrimiçi durumu yenilendi:', new Date().toLocaleTimeString());
    }, 5 * 60 * 1000); // 5 dakika
    
    // Component unmount olduğunda interval'ı temizle
    return () => {
      clearInterval(pingInterval);
      // Note: online_status'u false yapmıyoruz, bunun yerine server-side'da
      // son ping zamanına dayalı otomatik bir mekanizma kullanılması daha güvenli olacaktır
    };
  }, [currentUser?.id]);  // currentUser.id değiştiğinde bu effect çalışsın
  
  // Konuşmaları yükle
  useEffect(() => {
    if (!currentUser) return;
    
    // İlk kez yükleniyor mu?
    const isInitialLoad = conversations.length === 0;
    
    const loadConversations = async () => {
      // Sadece ilk yüklemede loading göster
      if (isInitialLoad) {
        setConversationsLoading(true);
      }
      
      try {
        const data = await fetchConversations();
        if (data) {
          setConversations(data);
        }
      } catch (error) {
        console.error('Konuşmalar yüklenirken hata:', error);
        toast.error('Sohbet listesi yüklenemedi');
      } finally {
        // Sadece ilk yüklemede loading'i kaldır
        if (isInitialLoad) {
          setConversationsLoading(false);
        }
      }
    };
    
    loadConversations();
    
    // Realtime subscriptions - metin değişikliklerini dinle
    const channel = supabase
      .channel('public:conversations')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'conversations',
      }, () => {
        // Konuşmalarda değişiklik olduğunda tekrar yükle
        // Gereksiz loading göstermeden konuşmaları güncelleyelim
        fetchConversations().then(data => {
          if (data && data.length > 0) {
            setConversations(data);
          }
        });
      })
      .subscribe();
      
    // Mesaj değişikliklerini dinle
    const messagesChannel = supabase
      .channel('chat_messages')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'messages',
      }, async (payload) => {
        // Yeni mesaj geldiğinde, sadece ilgili konuşmayı güncelle
        const newMessage = payload.new;
        
        if (!newMessage || !newMessage.conversation_id) return;
        
        // Konuşma ID'sini al
        const conversationId = newMessage.conversation_id;
        
        try {
          // Sadece etkilenen konuşmayı güncelle, tümünü tekrar yüklemeye gerek yok
          const { data: messageData } = await supabase
            .from('messages')
            .select('*')
            .eq('id', newMessage.id)
            .single();
          
          if (!messageData) return;
          
          // Gönderici bilgilerini al
          const { data: sender } = await supabase
            .from('user_profiles')
            .select('id, first_name, last_name')
            .eq('id', newMessage.sender_id)
            .single();
          
          // Konuşmaları güncelle, etkilenen konuşmayı en üste taşı
          setConversations(prevConversations => {
            // İlgili konuşmayı bul
            const conversationIndex = prevConversations.findIndex(
              c => c.conversation_id === conversationId
            );
            
            // Konuşma bulunamadıysa, değişiklik yapma
            if (conversationIndex === -1) return prevConversations;
            
            // Konuşma kopyasını oluştur
            const updatedConversations = [...prevConversations];
            
            // Güncellenen konuşmayı al
            const conversation = { ...updatedConversations[conversationIndex] };
            
            // Son mesaj bilgilerini güncelle
            conversation.last_message = messageData.message_text;
            conversation.last_message_at = messageData.created_at;
            conversation.last_message_sender_id = messageData.sender_id;
            
            // Kendimize ait olmayan mesajlar için okunmamış sayısını artır
            if (messageData.sender_id !== currentUser?.id) {
              // Sadece aktif sohbet değilse okunmamış sayısını artır
              if (activeChat !== conversationId) {
                conversation.unread_count = (conversation.unread_count || 0) + 1;
              }
              
              // Konuşma ID'sini vurgula
              setHighlightedChat(conversationId);
              
              // 2 saniye sonra vurgulamayı kaldır
              setTimeout(() => {
                setHighlightedChat(null);
              }, 2000);
            }
            
            // Güncellenmiş konuşmayı diziden çıkar
            updatedConversations.splice(conversationIndex, 1);
            
            // Konuşmayı en üste ekle
            return [conversation, ...updatedConversations];
          });
          
          // Mesaj mevcut kullanıcıdan değilse ses çal
          if (messageData.sender_id !== currentUser?.id) {
            playNotificationSound(messageData.sender_id);
          }
        } catch (error) {
          console.error('Yeni mesaj güncellenirken hata:', error);
        }
      })
      .subscribe();
      
    return () => {
      supabase.removeChannel(channel);
      supabase.removeChannel(messagesChannel);
    };
  }, [currentUser]);
  
  // Aktif sohbet değiştiğinde mesajları yükle
  useEffect(() => {
    if (!currentUser || !activeChat) return;
    
    const loadMessages = async () => {
      setMessagesLoading(true);
      setMessageOffset(0);
      
      try {
        const data = await fetchMessages(activeChat, 50, 0);
        if (data) {
          setMessages(data);
          setHasMoreMessages(data.length === 50);
          
          // Aktif sohbetteki okunmamış mesajları okundu olarak işaretle
          if (data.length > 0) {
            // Diğer kullanıcılardan gelen okunmamış mesajlar
            const unreadMessages = data.filter(
              msg => msg.sender_id !== currentUser?.id && !msg.is_read
            );
            
            // Her bir okunmamış mesajı okundu olarak işaretle
            unreadMessages.forEach(msg => {
              markMessageAsRead(msg.id);
            });
            
            // Aktif sohbetin okunmamış mesaj sayısını sıfırla
            setConversations(prevConversations => {
              return prevConversations.map(conv => {
                if (conv.conversation_id === activeChat) {
                  return {
                    ...conv,
                    unread_count: 0
                  };
                }
                return conv;
              });
            });
          }
        } else {
          setMessages([]);
          setHasMoreMessages(false);
        }
      } catch (error) {
        console.error('Mesajlar yüklenirken hata:', error);
        toast.error('Mesajlar yüklenemedi');
      } finally {
        setMessagesLoading(false);
      }
    };
    
    loadMessages();
    
    // Mesajları dinle
    const messageChannel = subscribeToConversation(activeChat, (newMessage) => {
      // Yeni mesajları sona ekle (en altta görünmesi için)
      setMessages(prevMessages => [...prevMessages, newMessage]);
      
      // Aktif sohbette yeni mesaj geldiğinde okunmamış sayısını sıfırla
      setConversations(prevConversations => {
        return prevConversations.map(conv => {
          if (conv.conversation_id === activeChat) {
            return {
              ...conv,
              unread_count: 0  // Aktif sohbette olduğumuz için okunmamış mesaj 0 olmalı
            };
          }
          return conv;
        });
      });
      
      // Mesaj bizim gönderdiğimiz değilse, okundu olarak işaretle
      if (newMessage.sender_id !== currentUser?.id) {
        markMessageAsRead(newMessage.id);
      }
    });
    
    return () => {
      if (messageChannel) {
        supabase.removeChannel(messageChannel);
      }
    };
  }, [activeChat, currentUser]);

  // Tüm kullanıcıları çek (mevcut kullanıcı hariç)
  useEffect(() => {
    if (!currentUser) return;
    
    const fetchUsers = async () => {
      try {
        setLoading(true);
        
        // Sadece erişime izin verilen kullanıcıları çek
        // Bu, organizasyon üyeleri, belirli roller veya iletişime geçilmesine izin verilen kullanıcılar olabilir
        // Örnek olarak, veritabanında bir "allowed_contacts" tablosu veya RLS politikası kullanılabilir
        // .eq('organization_id', currentUser?.organization_id)
        const { data, error } = await supabase
          .from('user_profiles')
          .select('id, first_name, last_name, email, profile_photo_url, job_position, online_status')
          .neq('id', currentUser.id)
          .order('first_name', { ascending: true });
          
        if (error) {
          throw error;
        }
        
        // Kullanıcı listesini hazırla
        const formattedUsers = data.map(user => ({
          id: user.id,
          name: `${user.first_name} ${user.last_name}`,
          avatar: user.profile_photo_url || null,
          online: user.online_status || false,
          unread: 0, // Şimdilik 0 olarak ayarla, sonra gerçek değeri çekilecek
          lastMessage: {
            text: 'Henüz mesaj yok',
            time: '',
            isNew: false
          },
          isTyping: false,
          email: user.email,
          job_position: user.job_position
        }));
        
        setUsers(formattedUsers);
        setFilteredUsers(formattedUsers);
      } catch (error) {
        console.error('Kullanıcılar yüklenemedi:', error);
        toast.error('Kullanıcı listesi yüklenemedi');
      } finally {
        setLoading(false);
      }
    };
    
    fetchUsers();
  }, [currentUser]);
  
  // Tüm kullanıcıları çek (mevcut kullanıcı olmasa bile)
  const fetchAllUsers = async () => {
    try {
      setLoading(true);
      
      // Sadece erişime izin verilen kullanıcıları çekin
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, first_name, last_name, email, profile_photo_url, job_position, online_status')
        // Aynı organizasyondaki veya departmandaki kullanıcıları çekmek için filtre ekleyebilirsiniz
        // .eq('organization_id', currentUser?.organization_id)
        .order('first_name', { ascending: true });
        
      if (error) {
        throw error;
      }
      
      // Kullanıcı listesini hazırla
      const formattedUsers = data.map(user => ({
        id: user.id,
        name: `${user.first_name} ${user.last_name}`,
        avatar: user.profile_photo_url || null,
        online: user.online_status || false,
        unread: 0,
        lastMessage: {
          text: 'Henüz mesaj yok',
          time: '',
          isNew: false
        },
        isTyping: false,
        email: user.email,
        job_position: user.job_position
      }));
      
      setAllUsers(formattedUsers);
      
      // Eğer currentUser yoksa, tüm kullanıcıları göster
      if (!currentUser) {
        setUsers(formattedUsers);
        setFilteredUsers(formattedUsers);
      }
    } catch (error) {
      console.error('Tüm kullanıcılar yüklenemedi:', error);
      toast.error('Kullanıcı listesi yüklenemedi');
    } finally {
      setLoading(false);
    }
  };
  
  // Yeni sohbet modalı açıldığında tüm kullanıcıları filtrele ve göster
  useEffect(() => {
    if (showNewChatModal) {
      // Modal açıldığında arama sorgusunu sıfırla
      setSearchQuery('');
      
      // Tüm kullanıcıları filtrele (currentUser hariç)
      if (currentUser) {
        const filtered = allUsers.filter(user => user.id !== currentUser.id);
        setFilteredUsers(filtered);
      } else {
        setFilteredUsers(allUsers);
      }
    }
  }, [showNewChatModal, allUsers, currentUser]);
  
  // Arama yapmak için arama sorgusuna göre filtreleme yap
  useEffect(() => {
    // Arama sorgusunun dolu olduğu durumda 
    if (searchQuery.trim() !== '') {
      const allUsersList = showNewChatModal ? allUsers : users;
      
      // Tüm kullanıcılar üzerinde arama yap
      const filtered = allUsersList.filter(user => 
        user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (user.email && user.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (user.job_position && user.job_position.toLowerCase().includes(searchQuery.toLowerCase()))
      );
      
      // Eğer modal açıksa ve currentUser varsa, o kullanıcıyı çıkar
      if (showNewChatModal && currentUser) {
        const withoutCurrentUser = filtered.filter(user => user.id !== currentUser.id);
        setFilteredUsers(withoutCurrentUser);
      } else {
        setFilteredUsers(filtered);
      }
    } else {
      // Arama sorgusu boşsa ve modal açıksa
      if (showNewChatModal) {
        if (currentUser) {
          const withoutCurrentUser = allUsers.filter(user => user.id !== currentUser.id);
          setFilteredUsers(withoutCurrentUser);
        } else {
          setFilteredUsers(allUsers);
        }
      } else {
        // Normal sohbet listesinde
        setFilteredUsers(users);
      }
    }
  }, [searchQuery, users, allUsers, showNewChatModal, currentUser]);

  // Find active chat details
  const allChats = [...filteredUsers, ...conversations];
  const currentChat = allChats.find(chat => chat?.conversation_id === activeChat);

  // Filtered chats based on selected filter
  const filteredChats = filter === 'all' 
    ? allChats 
    : filter === 'direct' 
      ? filteredUsers 
      : conversations;

  // Scroll to bottom of messages when messages change
  useEffect(() => {
    // Yeni mesaj geldiğinde yumuşak bir şekilde aşağı kaydır
    if (messages.length > 0) {
      setTimeout(() => {
        messageEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [messages]);

  // Ayrıca aktif sohbet değiştiğinde de aşağı kaydırmalıyız
  useEffect(() => {
    if (activeChat && messages.length > 0) {
      // Aktif sohbet değiştiğinde anında kaydır (smooth olmadan)
      messageEndRef.current?.scrollIntoView();
    }
  }, [activeChat]);

  // Handle message input changes with typing indicator
  const handleMessageChange = (e) => {
    setMessage(e.target.value);
    
    // Textarea'nın yüksekliğini içeriğine göre otomatik ayarla
    const textarea = e.target;
    
    // Önce yüksekliği sıfırla
    textarea.style.height = 'auto';
    
    // Yeni yüksekliği hesapla (max 120px)
    const newHeight = Math.min(textarea.scrollHeight, 120);
    
    // Yüksekliği ayarla
    textarea.style.height = newHeight + 'px';
    
    // Simulate "typing" status
    if (!isTyping) {
      setIsTyping(true);
    }
    
    // Clear previous timeout
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }
    
    // Set new timeout to stop "typing" after 1.5 seconds of inactivity
    const timeout = setTimeout(() => {
      setIsTyping(false);
    }, 1500);
    
    setTypingTimeout(timeout);
  };

  // Handle sending a message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim() || !activeChat) return;
    
    // Çok satırlı mesajı olduğu gibi koru, ancak fazladan boşlukları temizle
    const messageText = message.trim();
    setMessage('');
    setIsTyping(false);
    
    // Textarea'nın yüksekliğini sıfırla
    if (textareaRef.current) {
      textareaRef.current.style.height = '24px';
    }
    
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }
    
    try {
      const sentMessage = await sendMessage(activeChat, messageText);
      if (!sentMessage) {
        throw new Error('Mesaj gönderilemedi');
      }
      
      // Konuşma listesini gereksiz loading olmadan güncelle
      // Sadece bu aktif sohbetin son mesajını güncelleyelim
      setConversations(prevConversations => {
        return prevConversations.map(conv => {
          if (conv.conversation_id === activeChat) {
            // Son mesaj gösterilirken çok satırlı mesajın ilk satırını göster
            const firstLine = messageText.split('\n')[0];
            const displayMessage = firstLine + (messageText.includes('\n') ? '...' : '');
            
            return {
              ...conv,
              last_message: displayMessage,
              last_message_at: new Date().toISOString(),
              last_message_sender_id: currentUser?.id
            };
          }
          return conv;
        }).sort((a, b) => new Date(b.last_message_at) - new Date(a.last_message_at));
      });
      
    } catch (error) {
      console.error('Mesaj gönderme hatası:', error);
      toast.error('Mesaj gönderilemedi');
    }
  };

  // 6. Özel sohbet başlat
  const startPrivateChat = async (otherUserId) => {
    // Gerekli kontrolleri yap
    if (!currentUser?.id) {
      console.error('startPrivateChat: currentUser.id bulunamadı');
      return null;
    }
    
    if (!otherUserId) {
      console.error('startPrivateChat: otherUserId parametresi eksik');
      return null;
    }
    
    try {
      // console.log(`Özel sohbet başlatılıyor: ${currentUser.id} ile ${otherUserId} arasında`);
      
      // Önce var olan bir konuşma olup olmadığını kontrol et
      const { data: existingChats, error: checkError } = await supabase
        .from('conversation_members')
        .select(`
          conversation_id,
          user_id,
          conversations:conversation_id (
            is_group
          )
        `)
        .in('user_id', [currentUser.id, otherUserId]);
      
      if (checkError) {
        console.error('Mevcut sohbetler kontrol edilirken hata:', checkError);
        throw checkError;
      }
      
      //console.log('Bulunan sohbet üyelikleri:', existingChats);
      
      // Ortak bir özel sohbet bul
      if (existingChats && existingChats.length > 0) {
        // Kullanıcıya ait konuşmalar
        const userConversations = existingChats
          .filter(chat => chat.user_id === currentUser.id)
          .map(chat => chat.conversation_id);
        
        // Diğer kullanıcıya ait konuşmalar
        const otherUserConversations = existingChats
          .filter(chat => chat.user_id === otherUserId)
          .map(chat => chat.conversation_id);
        
        //console.log('Kullanıcı konuşmaları:', userConversations);
        //console.log('Diğer kullanıcı konuşmaları:', otherUserConversations);
        
        // Kesişim kümesini bul (ortak konuşmalar)
        const commonConversations = userConversations.filter(id => 
          otherUserConversations.includes(id)
        );
        
        //console.log('Ortak konuşmalar:', commonConversations);
        
        // Özel sohbet (grup olmayan) kontrol et
        if (commonConversations.length > 0) {
          // Her ortak konuşma için grup olup olmadığını kontrol et
          for (const convId of commonConversations) {
            // İlgili konuşmayı veritabanından doğrudan sorgulayalım
            const { data: conversation, error: convError } = await supabase
              .from('conversations')
              .select('id, is_group')
              .eq('id', convId)
              .single();
              
            if (convError) {
              console.error(`Konuşma bilgisi alınırken hata (ID: ${convId}):`, convError);
              continue; // Bir sonraki konuşmayı kontrol et
            }
            
            // console.log('Bulunan konuşma:', conversation);
            
            if (conversation && !conversation.is_group) {
              // console.log(`Mevcut özel sohbet bulundu: ${conversation.id}`);
              return conversation.id;
            }
          }
        }
      }
      
      // console.log('Mevcut bir sohbet bulunamadı, yeni bir sohbet oluşturuluyor');
      
      // Yeni bir sohbet oluştur
      const { data: newConversation, error: convError } = await supabase
        .from('conversations')
        .insert({
          title: null, // Özel sohbetlerde başlık null olur
          is_group: false,
          created_by: currentUser.id,
          last_message_at: new Date().toISOString() // son mesaj zamanını şu anki zaman olarak ayarla
        })
        .select()
        .single();
      
      if (convError) {
        console.error('Konuşma oluşturulurken hata:', convError);
        throw convError;
      }
      
      //console.log(`Yeni konuşma oluşturuldu. ID: ${newConversation.id}`);
      
      // Konuşma üyelerini ekle
      const members = [
        { conversation_id: newConversation.id, user_id: currentUser.id },
        { conversation_id: newConversation.id, user_id: otherUserId }
      ];
      
      const { error: memberError } = await supabase
        .from('conversation_members')
        .insert(members);
      
      if (memberError) {
        console.error('Konuşma üyeleri eklenirken hata:', memberError);
        
        // Hata oluştuğunda, yarım kalan konuşmayı sil
        const { error: deleteError } = await supabase
          .from('conversations')
          .delete()
          .eq('id', newConversation.id);
          
        if (deleteError) {
          console.error(`Hatalı konuşma silinirken hata (ID: ${newConversation.id}):`, deleteError);
        }
        
        throw memberError;
      }
      
      // console.log('Konuşma üyeleri başarıyla eklendi');
      return newConversation.id;
    } catch (error) {
      console.error('Özel sohbet başlatılırken hata:', error);
      throw error;
    }
  };

  // Yeni sohbet başlatma işlemi
  const handleStartNewChat = async (user) => {
    if (!currentUser?.id) return;
    
    try {
      toast.loading('Sohbet başlatılıyor...');
      
      // console.log(`Starting chat with user: ${user.name} (${user.id})`);
      
      const conversationId = await startPrivateChat(user.id);
      if (conversationId) {
        toast.dismiss();
        setActiveChat(conversationId);
        setShowNewChatModal(false);
        toast.success(`${user.name} ile sohbet başlatıldı`);
        
        // Konuşmaları tekrar yükle
        const newConversations = await fetchConversations();
        if (newConversations) {
          setConversations(newConversations);
        }
      } else {
        throw new Error('Sohbet başlatılamadı');
      }
    } catch (error) {
      toast.dismiss();
      console.error('Sohbet başlatma hatası:', error);
      toast.error('Sohbet başlatılamadı: ' + error.message);
    }
  };

  // Get avatar initials for groups without avatars
  const getInitials = (name) => {
    if (!name) return '';
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase();
  };

  // 7. Grup sohbeti oluştur
  const createGroupChat = async (groupName, memberIds) => {
    if (!currentUser?.id) return null;
    
    try {
      // console.log(`Creating group chat "${groupName}" with ${memberIds.length} members`);
      
      // Önce grup konuşması oluştur
      const { data: newGroup, error: groupError } = await supabase
        .from('conversations')
        .insert({
          title: groupName,
          is_group: true,
          created_by: currentUser.id
        })
        .select()
        .single();
      
      if (groupError) {
        console.error('Error creating group conversation:', groupError);
        throw groupError;
      }
      
      // console.log(`Created new group with ID: ${newGroup.id}`);
      
      // Kendimizi de ekleyelim
      const allMemberIds = [currentUser.id, ...memberIds];
      
      // Üyeleri ekle
      const members = allMemberIds.map(userId => ({
        conversation_id: newGroup.id,
        user_id: userId
      }));
      
      const { error: memberError } = await supabase
        .from('conversation_members')
        .insert(members);
      
      if (memberError) {
        console.error('Error adding members to group:', memberError);
        throw memberError;
      }

      // Grup oluşturuldu sistem mesajı gönder
      try {
        // Kullanıcı bilgilerini al
        const { data: userData } = await supabase
          .from('user_profiles')
          .select('first_name, last_name')
          .eq('id', currentUser.id)
          .single();
        
        const creatorFullName = userData 
          ? `${userData.first_name} ${userData.last_name}` 
          : 'Bir kullanıcı';

        const systemMessage = `${creatorFullName} "${groupName}" grubunu oluşturdu.`;
        
        // Sistem mesajını ekliyoruz
        await supabase
          .from('messages')
          .insert({
            conversation_id: newGroup.id,
            sender_id: currentUser.id,
            message_text: systemMessage,
            message_type: 'system'
          });
      } catch (systemMsgError) {
        console.error('Sistem mesajı eklenirken hata oluştu:', systemMsgError);
        // Sistem mesajı eklenemese bile grup oluşturma işlemine devam ediyoruz
      }
      
      // console.log('Group members added successfully');
      return newGroup.id;
    } catch (error) {
      console.error('Error creating group chat:', error);
      throw error;
    }
  };

  // Grup oluşturma formu gönderildiğinde
  const handleCreateGroup = async () => {
    if (!groupName.trim()) {
      toast.error('Lütfen bir grup adı girin');
      return;
    }
    
    if (selectedGroupMembers.length === 0) {
      toast.error('Lütfen en az bir üye seçin');
      return;
    }
    
    try {
      setGroupCreating(true);
      
      const selectedMemberIds = selectedGroupMembers.map(member => member.id);
      const groupId = await createGroupChat(groupName, selectedMemberIds);
      
      if (groupId) {
        // Eğer grup fotoğrafı seçildiyse yükle
        if (groupPhoto) {
          await updateGroupPhoto(groupId, groupPhoto);
        }
        
        // Gereksiz loading olmadan direkt olarak grupla sohbeti başlat
        const newConversations = await fetchConversations();
        
        // Yeni sohbet listesini set et ama loading gösterme
        if (newConversations && newConversations.length > 0) {
          setConversations(newConversations);
          
          // Yeni oluşturulan grubu aktif olarak seç
          setActiveChat(groupId);
          setShowNewChatModal(false);
          setChatModalTab('direct');
          setGroupName('');
          setSelectedGroupMembers([]);
          setGroupPhoto(null);
          setGroupPhotoPreview(null);
          
          toast.success('Grup oluşturuldu');
        }
      } else {
        throw new Error('Grup oluşturulamadı');
      }
    } catch (error) {
      console.error('Grup oluşturma hatası:', error);
      toast.error('Grup oluşturulamadı: ' + error.message);
    } finally {
      setGroupCreating(false);
    }
  };

  // Kişi grup üyeliğini değiştir
  const toggleGroupMember = (user) => {
    // Kullanıcı zaten seçiliyse, listeden çıkar
    if (selectedGroupMembers.some(member => member.id === user.id)) {
      setSelectedGroupMembers(selectedGroupMembers.filter(member => member.id !== user.id));
    } else {
      // Kullanıcı seçili değilse, listeye ekle
      setSelectedGroupMembers([...selectedGroupMembers, user]);
    }
  };

  // Seçili grup üyesini kaldır
  const removeGroupMember = (userId) => {
    setSelectedGroupMembers(selectedGroupMembers.filter(member => member.id !== userId));
  };

  // 1. Konuşmaları getir
  const fetchConversations = async () => {
    if (!currentUser?.id) return [];
    
    try {
      // Step 1: Get conversation IDs without using foreign key relationships
      const { data: memberData, error: memberError } = await supabase
        .from('conversation_members')
        .select('conversation_id')
        .eq('user_id', currentUser.id);
      
      if (memberError) {
        console.error('Konuşma üyelikleri alınamadı:', memberError);
        return [];
      }
      
      if (!memberData || memberData.length === 0) {
        return [];
      }
      
      // Extract conversation IDs
      const conversationIds = memberData.map(item => item.conversation_id);
      
      // Step 2: Get conversation details in a separate query
      const { data: conversationsData, error: conversationsError } = await supabase
      .from('conversations')
      .select('id, title, is_group, created_at, updated_at, group_photo_url, metadata')
        .in('id', conversationIds);
      
      if (conversationsError) {
        console.error('Konuşma detayları alınamadı:', conversationsError);
        return [];
      }
      
      // Get additional info for each conversation
      const conversations = [];
      
      for (const conversation of conversationsData) {
        // Step 3: Get last message for this conversation
        const { data: messageData, error: messageError } = await supabase
          .from('messages')
          .select('*')
          .eq('conversation_id', conversation.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        
        // Step 4: Get unread count - message_status tablosunu kullan
        let unreadCount = 0;
        try {
          // Doğrudan bir SQL sorgusu kullanarak messages ve message_status tablolarını birleştir
          const { data: msgData, error: msgCountError } = await supabase
            .from('messages')
            .select(`
              id,
              message_status!left(
                is_read,
                user_id
              )
            `)
            .eq('conversation_id', conversation.id)
            .neq('sender_id', currentUser.id);
            
          if (!msgCountError && msgData) {
            // Okunmamış mesajları say
            unreadCount = msgData.filter(msg => {
              // message_status dizisini kontrol et
              if (!msg.message_status || msg.message_status.length === 0) {
                return true; // Hiç durum kaydı yoksa okunmamış sayılır
              }
              
              // Mevcut kullanıcı için okunma durumunu kontrol et
              return !msg.message_status.some(status => 
                status.user_id === currentUser.id && status.is_read === true
              );
            }).length;
            
            // console.log(`Found ${unreadCount} unread messages for conversation ${conversation.id}`);
          } else {
            console.warn('Unread count query failed:', msgCountError);
          }
        } catch (unreadError) {
          console.warn('Okunmamış mesaj sayısı alınamadı:', unreadError);
        }
        
        // Step 5: Get members of conversation without foreign key relationships
        const { data: membersList, error: membersError } = await supabase
          .from('conversation_members')
          .select('user_id')
          .eq('conversation_id', conversation.id);
        
        if (membersError) {
          console.warn('Konuşma üyeleri alınamadı:', membersError);
        }
        
        // Get user details for each member
        const members = [];
        if (membersList && membersList.length > 0) {
          const userIds = membersList.map(member => member.user_id);
          
          const { data: usersData, error: usersError } = await supabase
            .from('user_profiles')
            .select('id, first_name, last_name, profile_photo_url, online_status')
            .in('id', userIds);
          
          if (!usersError && usersData) {
            for (const user of usersData) {
              members.push({
                id: user.id,
                name: `${user.first_name} ${user.last_name}`,
                avatar: user.profile_photo_url,
                online: user.online_status
              });
            }
          }
        }
        
        // Format the conversation object
        const conversationObj = {
          conversation_id: conversation.id,
          id: conversation.id,
          title: conversation.title || 'Adsız Konuşma',
          is_group: conversation.is_group,
          created_at: conversation.created_at,
          updated_at: conversation.updated_at,
          group_photo_url: conversation.group_photo_url || null,
          metadata: conversation.metadata || null,  // Bu satırı ekle
          last_message: messageData?.message_text || null,
          last_message_at: messageData?.created_at || conversation.created_at,
          unread_count: unreadCount || 0,
          members: members
        };
        
        // Set appropriate title for direct chats (non-group)
        if (!conversation.is_group && members.length > 0) {
          // Find the other user (not the current user)
          const otherUser = members.find(member => member.id !== currentUser.id);
          if (otherUser) {
            conversationObj.title = otherUser.name;
            conversationObj.otherUser = otherUser; // Karşı kullanıcı bilgilerini sakla
          }
        }
        
        conversations.push(conversationObj);
      }
      
      // Sort conversations by last message time, newest first
      return conversations.sort((a, b) => 
        new Date(b.last_message_at) - new Date(a.last_message_at)
      );
    } catch (error) {
      console.error('Konuşmalar yüklenemedi:', error);
      return [];
    }
  };

  // 2. Konuşma mesajlarını getir
  const fetchMessages = async (conversationId, limit = 50, offset = 0) => {
    if (!currentUser?.id) return [];
    
    try {
      // console.log(`Fetching messages for conversation ${conversationId} (limit: ${limit}, offset: ${offset})`);
      
      // Doğrudan SQL sorgusu kullan - RPC yerine
      const { data: messages, error: msgError } = await supabase
        .from('messages')
        .select(`
          *,
          sender:sender_id (
            id, 
            first_name, 
            last_name, 
            profile_photo_url
          ),
          message_status!left(
            is_read,
            user_id
          )
        `)
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true }) // Değiştirildi: en eski mesajlar önce
        .range(offset, offset + limit - 1);
      
      if (msgError) {
        console.error('Error fetching messages:', msgError);
        return [];
      }
      
      // Mesajların okunma durumlarını kontrol et ve formatla
      const formattedMessages = messages.map(msg => {
        // Varsayılan olarak kendi mesajlarımız okunmuş sayılır
        let isRead = msg.sender_id === currentUser.id;
        
        // Diğer mesajlar için okunma durumunu kontrol et
        if (!isRead && msg.message_status) {
          // Mevcut kullanıcının okunma durumunu kontrol et
          const userReadStatus = msg.message_status.find(
            status => status.user_id === currentUser.id
          );
          
          if (userReadStatus) {
            isRead = userReadStatus.is_read;
          }
        }
        
        return {
          message_id: msg.id,
          conversation_id: msg.conversation_id,
          sender_id: msg.sender_id,
          sender_name: `${msg.sender.first_name} ${msg.sender.last_name}`,
          sender_avatar: msg.sender.profile_photo_url,
          message_text: msg.message_text,
          message_type: msg.message_type,
          created_at: msg.created_at,
          is_read: isRead
        };
      });
      
      // console.log(`Successfully fetched ${formattedMessages.length} messages`);
      return formattedMessages;
    } catch (error) {
      console.error('Error fetching messages:', error);
      return [];
    }
  };

  // Daha fazla mesaj yükle
  const loadMoreMessages = async () => {
    if (!activeChat || !hasMoreMessages || messagesLoading) return;
    
    // Mesaj sıralaması düzeltildiği için (eskiden yeniye), daha ESKI mesajları yüklemeliyiz
    // Bu durumda mevcut offset mantığı doğru çalışır (daha eski mesajlar için offset arttırılır)
    const newOffset = messageOffset + 50;
    setMessagesLoading(true);
    
    try {
      const data = await fetchMessages(activeChat, 50, newOffset);
      if (data) {
        // Daha eski mesajları mevcut mesajların sonuna ekle
        setMessages(prevMessages => [...prevMessages, ...data]);
        setHasMoreMessages(data.length === 50);
        setMessageOffset(newOffset);
      }
    } catch (error) {
      console.error('Daha fazla mesaj yüklenirken hata:', error);
      toast.error('Daha fazla mesaj yüklenemedi');
    } finally {
      setMessagesLoading(false);
    }
  };

  // 3. Mesaj gönder
  const sendMessage = async (conversationId, messageText) => {
    if (!currentUser?.id) return null;
    
    // Konuşmayı bul
    const conversation = conversations.find(c => c.conversation_id === conversationId);
    
    // Eğer bireysel sohbet ise engelleme kontrolü yap
    if (conversation && !conversation.is_group && conversation.otherUser) {
      const otherUserId = conversation.otherUser.id;
      
      // Karşı taraf engellenmiş mi?
      if (blockedUsers.includes(otherUserId)) {
        toast.error('Engellediğiniz bir kullanıcıya mesaj gönderemezsiniz. Önce engeli kaldırın.');
        return null;
      }
      
      // Karşı taraf bizi engellenmiş mi? (Veritabanından kontrol et)
      try {
        const { data, error } = await supabase
          .from('user_blocks')
          .select('id')
          .eq('user_id', otherUserId)
          .eq('blocked_user_id', currentUser.id)
          .maybeSingle();
          
        if (error) {
          console.error('Karşı taraf engel kontrolü hatası:', error);
        } else if (data) {
          toast.error('Bu kullanıcı size mesaj göndermenizi engelliyor.');
          return null;
        }
      } catch (error) {
        console.error('Engel kontrolü sırasında hata:', error);
      }
    }
    
    // Engelleme yoksa mesajı gönder
    const { data, error } = await supabase
      .from('messages')
      .insert({
        conversation_id: conversationId,
        sender_id: currentUser.id,
        message_text: messageText,
        message_type: 'text'
      })
      .select()
      .single();
    
    if (error) {
      console.error('Mesaj gönderilemedi:', error);
      return null;
    }
    return data;
  };

  // 4. Dosya/fotoğraf eklentiyi yükle ve gönder
  const sendAttachment = async (conversationId, file) => {
    // Dosya türüne göre mesaj metni belirle
    let messageText = '';
    let messageType = '';
    
    // Dosya türüne göre mesaj ve tip belirle
    // IMPORTANT: message_type can only be 'text', 'image', or 'file' according to database schema
    if (file.type.startsWith('image/')) {
      messageText = 'Fotoğraf gönderildi';
      messageType = 'image';
    } else if (file.type.includes('pdf') || file.name.endsWith('.pdf')) {
      messageText = 'PDF belgesi gönderildi';
      messageType = 'file'; // Changed from 'document' to 'file'
    } else if (file.type.includes('word') || file.name.match(/\.(doc|docx)$/i)) {
      messageText = 'Word belgesi gönderildi';
      messageType = 'file'; // Changed from 'document' to 'file'
    } else if (file.type.includes('text') || file.name.match(/\.(txt|rtf)$/i)) {
      messageText = 'Metin belgesi gönderildi';
      messageType = 'file'; // Changed from 'document' to 'file'
    } else if (file.type.includes('spreadsheet') || file.type.includes('excel') || file.name.match(/\.(xls|xlsx|csv)$/i)) {
      messageText = 'Excel belgesi gönderildi';
      messageType = 'file'; // Changed from 'document' to 'file'
    } else if (file.type.includes('presentation') || file.type.includes('powerpoint') || file.name.match(/\.(ppt|pptx)$/i)) {
      messageText = 'Sunum belgesi gönderildi';
      messageType = 'file'; // Changed from 'document' to 'file'
    } else {
      messageText = 'Dosya gönderildi';
      messageType = 'file';
    }
    
    // Önce mesaj oluştur
    const { data: message, error: messageError } = await supabase
      .from('messages')
      .insert({
        conversation_id: conversationId,
        sender_id: currentUser.id,
        message_text: messageText,
        message_type: messageType // This must match database constraints
      })
      .select()
      .single();
    
    if (messageError) {
      console.error('Mesaj oluşturulamadı:', messageError);
      throw new Error(`Mesaj oluşturulamadı: ${messageError.message || 'Bilinmeyen hata'}`);
    }
    
    try {
      // Dosyayı benzersiz bir isimle kaydet - dosya adındaki özel karakterleri temizle
      const fileExt = file.name.split('.').pop();
      const sanitizedName = Date.now() + '-' + file.name.replace(/[^a-zA-Z0-9]/g, '_');
      const fileName = `${conversationId}/${message.id}/${sanitizedName}`;
      
      //console.log('Dosya yükleniyor:', fileName);
      
      // Bucket izinlerinin public olup olmadığını kontrol et
      const { data: bucketData } = await supabase.storage.getBucket('chat_media');
      //console.log('Bucket bilgileri:', bucketData);
      
      // Dosyayı yükle ve HERKES için erişilebilir yap
      const { data: fileData, error: fileError } = await supabase
        .storage
        .from('chat_media')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false,
          contentType: file.type
        });
      
      if (fileError) {
        console.error('Dosya yüklenemedi:', fileError);
        throw new Error(`Dosya yüklenemedi: ${fileError.message || 'Bilinmeyen hata'}`);
      }
      
      //console.log('Dosya başarıyla yüklendi:', fileData);
      
      // Public URL'i al (herkes tarafından erişilebilir)
      const publicUrlData = supabase
        .storage
        .from('chat_media')
        .getPublicUrl(fileName);
  
      const publicUrl = publicUrlData?.data?.publicUrl;
      
      if (!publicUrl) {
        console.error('Public URL alınamadı:', publicUrlData);
        throw new Error('Geçerli bir dosya URL\'i oluşturulamadı');
      }
      
      //console.log('Public URL alındı:', publicUrl);
      
      // Dosya izinlerini ayarla - herkese açık hale getir
      try {
        const { error: anonError } = await supabase.storage.from('chat_media').setAnon(fileName, true);
        if (anonError) {
          console.warn('Dosya izinleri değiştirilemedi:', anonError);
        }
      } catch (permError) {
        console.warn('Dosya izinleri ayarlanamadı:', permError);
      }
      
      // Mesaja eklenti ekle
      const { data: attachment, error: attachmentError } = await supabase
        .from('message_attachments')
        .insert({
          message_id: message.id,
          file_name: file.name,
          file_size: file.size,
          file_type: file.type,
          file_url: publicUrl,
          public_url: publicUrl,
          thumbnail_url: file.type.startsWith('image/') ? publicUrl : null
        })
        .select(); // Eklenen veriyi geri almak için select() ekle
      
      if (attachmentError) {
        console.error('Eklenti eklenemedi:', attachmentError);
        throw new Error(`Eklenti eklenemedi: ${attachmentError.message || 'Bilinmeyen hata'}`);
      }
      
      //console.log('Eklenti başarıyla oluşturuldu:', attachment);
      
      // Mesaj verisine eklenti bilgilerini ekle
      return {
        ...message,
        attachment: attachment && attachment.length > 0 
          ? {
              ...attachment[0],
              // Eklenti objesi içinde özel olarak publicUrl bilgisini saklayalım
              _publicUrl: publicUrl
            }
          : {
              message_id: message.id,
              file_name: file.name,
              file_size: file.size,
              file_type: file.type,
              file_url: publicUrl,
              _publicUrl: publicUrl,
              public_url: publicUrl,
              thumbnail_url: file.type.startsWith('image/') ? publicUrl : null
            }
      };
    } catch (error) {
      // Hata durumunda mesajı silmeyi dene
      console.error('Dosya yükleme hatası, mesaj silinecek:', error);
      try {
        await supabase.from('messages').delete().eq('id', message.id);
      } catch (deleteError) {
        console.error('Hatalı mesaj silinemedi:', deleteError);
      }
      
      // Orijinal hatayı tekrar fırlat
      throw error;
    }
  };

  // 5. Realtime aboneliği oluştur
  const subscribeToConversation = (conversationId, onNewMessage) => {
    if (!conversationId) {
      console.warn('Cannot subscribe to conversation: No conversation ID provided');
      return null;
    }

    try {
      // Create a channel for this conversation
      const channel = supabase
        .channel(`conversation:${conversationId}`)
        .on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversationId}`
        }, payload => {
          // Process the new message
          const newMessage = payload.new;
          
          // If the message is not from the current user, mark it as read
          if (newMessage.sender_id !== currentUser?.id) {
            // Mark the message as read
            markMessageAsRead(newMessage.id).catch(err => {
              console.error('Error marking message as read:', err);
            });
          }
          
          // Enrich the message with additional info before sending it to the callback
          enrichMessageData(newMessage).then(enrichedMessage => {
            if (enrichedMessage) {
              // Call the callback with the enriched message
              onNewMessage(enrichedMessage);
              
              // Play notification sound for messages from others
              if (enrichedMessage.sender_id !== currentUser?.id) {
                playNotificationSound(enrichedMessage.sender_id);
              }
            }
          });
        })
        .subscribe();
      
      return channel;
    } catch (error) {
      console.error('Error subscribing to conversation:', error);
      return null;
    }
  };

  // Helper function to enrich message data
  const enrichMessageData = async (message) => {
    if (!message) return null;
    
    try {
      // Get sender info
      const { data: senderData, error: senderError } = await supabase
        .from('user_profiles')
        .select('id, first_name, last_name, profile_photo_url')
        .eq('id', message.sender_id)
        .single();
      
      if (senderError) {
        console.warn('Sender data could not be fetched:', senderError);
        // Hata durumunda undefined değerleri döndürmek yerine boş bir nesne kullan
        // Bu, görsel bileşenlerin çökmesini engeller
        return {
          ...message,
          sender_name: 'Bilinmeyen Kullanıcı',
          sender_avatar: null,
          is_read: message.sender_id === currentUser?.id // Kendi mesajlarımız okunmuş sayılır
        };
      }
      
      // Kullanıcının kendi mesajları her zaman okunmuş sayılır
      let isRead = message.sender_id === currentUser?.id;
      
      // Diğer kullanıcılardan gelen mesajlar için okunma durumunu kontrol et
      if (!isRead && currentUser?.id && message.id) {
        try {
          // message_status tablosundan okunma durumunu kontrol et
          const { data: readStatus, error: readError } = await supabase
            .from('message_status')
            .select('is_read')
            .eq('message_id', message.id)
            .eq('user_id', currentUser.id)
            .maybeSingle();
            
          if (!readError && readStatus) {
            isRead = readStatus.is_read;
          }
        } catch (readCheckError) {
          console.error('Error checking message read status:', readCheckError);
          // Hata durumunda varsayılan okunma durumunu kullan
        }
      }
      
      // Eğer mesajda eklenti yoksa ve mesaj tipi bir dosya ya da resim ise
      if ((message.message_type === 'file' || message.message_type === 'image') && !message.attachment) {
        try {
          //console.log('Mesaj için eklenti aranıyor, ID:', message.id);
          
          // İlişkili eklentiyi ara
          const { data: attachmentData, error: attachmentError } = await supabase
            .from('message_attachments')
            .select('*')
            .eq('message_id', message.id)
            .maybeSingle();
          
          if (!attachmentError && attachmentData) {
            //console.log('Eklenti başarıyla bulundu:', attachmentData);
            message.attachment = attachmentData;
            
            // Eğer eklenti URL'leri yoksa, doğrudan oluşturalım
            if (!attachmentData.file_url && !attachmentData.public_url) {
              console.warn('Eklenti URL\'leri eksik, oluşturuluyor...');
              
              // Storage URL'lerini oluştur
              const baseUrl = import.meta.env.VITE_SUPABASE_URL;
              const bucketName = 'chat_media';
              
              // Mesaj için sohbet ID'si
              const { data: msgData } = await supabase
                .from('messages')
                .select('conversation_id')
                .eq('id', message.id)
                .single();
                
              if (msgData && msgData.conversation_id) {
                const fileName = `${msgData.conversation_id}/${message.id}/${attachmentData.file_name}`;
                const publicUrl = `${baseUrl}/storage/v1/object/public/${bucketName}/${fileName}`;
                
                //console.log('Oluşturulan dosya URL\'si:', publicUrl);
                message.attachment.file_url = publicUrl;
                message.attachment.public_url = publicUrl;
                
                if (message.message_type === 'image') {
                  message.attachment.thumbnail_url = publicUrl;
                }
              }
            }
          } else {
            console.error('Eklenti bulunamadı veya hata:', attachmentError);
          }
        } catch (attachmentFetchError) {
          console.error('Error fetching message attachment:', attachmentFetchError);
        }
      }
      
      // Return enriched message
      return {
        ...message,
        sender_name: `${senderData.first_name} ${senderData.last_name}`,
        sender_avatar: senderData.profile_photo_url,
        is_read: isRead
      };
    } catch (error) {
      console.error('Error enriching message data:', error);
      // Veri zenginleştirilemezse en azından iletinin kendisini dön
      return message;
    }
  };

  // Helper function to mark a message as read
  const markMessageAsRead = async (messageId) => {
    try {
      if (!currentUser?.id || !messageId) return;
      
      // 1. Mesaj tablosunda güncelleme yap
      const { error: messageError } = await supabase
        .from('messages')
        .update({ is_read: true })
        .eq('id', messageId);
        
      if (messageError) throw messageError;
      
      // 2. message_status tablosunda kontrol et veya oluştur
      const { data: existingStatus, error: checkError } = await supabase
        .from('message_status')
        .select('*')
        .eq('message_id', messageId)
        .eq('user_id', currentUser.id)
        .maybeSingle();
        
      if (checkError) {
        console.error('Mesaj durumu kontrol edilirken hata:', checkError);
        
        // Tablo henüz yoksa oluştur
        if (checkError.code === '42P01') { // Tablo yok hatası
          // console.log('message_status tablosu bulunamadı, oluşturuluyor...');
          
          // Tablo oluştur
          const { error: createError } = await supabase.rpc('create_message_status_table');
          
          if (createError) {
            console.error('Tablo oluşturma hatası:', createError);
          }
        }
      }
      
      if (existingStatus) {
        // Varsa güncelle
        const { error: updateError } = await supabase
          .from('message_status')
          .update({ is_read: true, read_at: new Date().toISOString() })
          .eq('message_id', messageId)
          .eq('user_id', currentUser.id);
          
        if (updateError) throw updateError;
      } else {
        // Yoksa yeni kayıt oluştur
        const { error: insertError } = await supabase
          .from('message_status')
          .insert({
            message_id: messageId,
            user_id: currentUser.id,
            is_read: true,
            read_at: new Date().toISOString()
          });
          
        if (insertError) throw insertError;
      }
      
      // 3. Mesajları state'te güncelle
      setMessages(prevMessages => prevMessages.map(msg => 
        msg.id === messageId 
          ? { ...msg, is_read: true }
          : msg
      ));
      
      return true;
    } catch (error) {
      console.error('Mesaj okundu olarak işaretlenirken hata:', error);
      return false;
    }
  };
  
  // Gruptan ayrılma fonksiyonu
  const leaveGroup = async (groupId) => {
    try {
      // Kullanıcı bilgilerini al
      const { data: userData } = await supabase
        .from('user_profiles')
        .select('first_name, last_name')
        .eq('id', currentUser.id)
        .single();
      
      const leaverFullName = userData 
        ? `${userData.first_name} ${userData.last_name}` 
        : 'Bir kullanıcı';
      
      // Gruptan ayrılma sistem mesajı gönder
      await supabase
        .from('messages')
        .insert({
          conversation_id: groupId,
          sender_id: currentUser.id,
          message_text: `${leaverFullName} gruptan ayrıldı.`,
          message_type: 'system'
        });
      
      // Konuşmadaki üyeliğimizi sil
      const { error } = await supabase
        .from('conversation_members')
        .delete()
        .eq('conversation_id', groupId)
        .eq('user_id', currentUser.id);
        
      if (error) throw error;
      
      toast.success('Gruptan ayrıldınız');
      
      // Aktif sohbeti sıfırla ve sohbet listesini güncelle
      setActiveChat(null);
      fetchConversations();
    } catch (error) {
      console.error('Gruptan ayrılırken hata:', error);
      toast.error('Gruptan ayrılırken bir hata oluştu');
    }
  };
  
  // Grup adını düzenleme
  const updateGroupName = async (groupId, newName) => {
    try {
      const { error } = await supabase
        .from('conversations')
        .update({ title: newName })
        .eq('id', groupId);
        
      if (error) throw error;
      
      toast.success('Grup adı güncellendi');
      
      // Konuşmaları güncelle
      fetchConversations();
      setIsEditingGroupName(false);
    } catch (error) {
      console.error('Grup adı güncellenirken hata:', error);
      toast.error('Grup adı güncellenemedi');
    }
  };
  
  // Potansiyel grup üyelerini getir
  const fetchPotentialGroupMembers = async (groupId) => {
    try {
      // Mevcut grup üyelerini al
      const { data: membersList, error: membersError } = await supabase
        .from('conversation_members')
        .select('user_id')
        .eq('conversation_id', groupId);
        
      if (membersError) throw membersError;
      
      // Mevcut üye id'leri
      const memberIds = membersList.map(member => member.user_id);
      
      // Tüm kullanıcıları getir
      const { data: allAvailableUsers, error: usersError } = await supabase
        .from('user_profiles')
        .select('id, first_name, last_name, email, profile_photo_url, job_position, online_status')
        .neq('id', currentUser.id)
        .not('id', 'in', `(${memberIds.join(',')})`);
        
      if (usersError) throw usersError;
      
      // Kullanıcı listesini formatlama
      const formattedUsers = allAvailableUsers.map(user => ({
        id: user.id,
        name: `${user.first_name} ${user.last_name}`,
        avatar: user.profile_photo_url || null,
        email: user.email,
        job_position: user.job_position,
        online: user.online_status || false,
        selected: false
      }));
      
      setPotentialMembers(formattedUsers);
    } catch (error) {
      console.error('Potansiyel üyeler getirilirken hata:', error);
      toast.error('Kullanıcı listesi yüklenemedi');
    }
  };
  
  // Gruba üye ekleme
  const addMembersToGroup = async (groupId) => {
    try {
      if (selectedNewMembers.length === 0) {
        toast.info('Lütfen en az bir üye seçin');
        return;
      }
      
      // Kullanıcı bilgilerini al
      const { data: userData } = await supabase
        .from('user_profiles')
        .select('first_name, last_name')
        .eq('id', currentUser.id)
        .single();
      
      const adderFullName = userData 
        ? `${userData.first_name} ${userData.last_name}` 
        : 'Bir kullanıcı';
      
      // Yeni üyelerin adlarını al
      const memberNames = selectedNewMembers.map(member => member.name).join(', ');
      
      // Yeni üyeleri conversation_members tablosuna ekle
      const membersToAdd = selectedNewMembers.map(member => ({
        conversation_id: groupId,
        user_id: member.id
      }));
      
      const { error: insertError } = await supabase
        .from('conversation_members')
        .insert(membersToAdd);
        
      if (insertError) throw insertError;
      
      // Üye ekleme sistem mesajı
      await supabase
        .from('messages')
        .insert({
          conversation_id: groupId,
          sender_id: currentUser.id,
          message_text: `${adderFullName} gruba ${selectedNewMembers.length > 1 ? 'yeni üyeler' : 'yeni üye'} ekledi: ${memberNames}`,
          message_type: 'system'
        });
      
      // Her yeni üye için ayrı katılma mesajı
      for (const member of selectedNewMembers) {
        await supabase
          .from('messages')
          .insert({
            conversation_id: groupId,
            sender_id: currentUser.id,
            message_text: `${member.name} gruba katıldı`,
            message_type: 'system'
          });
      }
      
      toast.success(`${selectedNewMembers.length} yeni üye gruba eklendi`);
      
      // State'leri temizle
      setSelectedNewMembers([]);
      setShowAddMemberModal(false);
      
      // Konuşmaları güncelle
      await fetchConversations();
      
      // Eğer mevcut grupta ise, grup bilgilerini ve mesajları da güncelle
      if (activeChat && activeChat.conversation_id === groupId) {
        // Aktif konuşmayı tüm konuşmalar içinden bul ve güncelle
        const updatedChat = conversations.find(conv => conv.conversation_id === groupId);
        if (updatedChat) {
          setActiveChat(updatedChat);
        }
        
        // Mesajları yeniden yükle
        loadMessages();
      }
    } catch (error) {
      console.error('Üye eklenirken hata:', error);
      toast.error('Üyeler eklenemedi');
    }
  };
  
  // Bildirim sesini kapat/aç
  const toggleMuteChat = (chatId) => {
    // Mevcut durumu kontrol et
    const isMuted = mutedChats.includes(chatId);
    
    if (isMuted) {
      // Bildirimleri aç
      const updatedMutedChats = mutedChats.filter(id => id !== chatId);
      setMutedChats(updatedMutedChats);
      localStorage.setItem('mutedChats', JSON.stringify(updatedMutedChats));
      toast.success('Bildirimler açıldı');
    } else {
      // Bildirimleri kapat
      const updatedMutedChats = [...mutedChats, chatId];
      setMutedChats(updatedMutedChats);
      localStorage.setItem('mutedChats', JSON.stringify(updatedMutedChats));
      toast.success('Bildirimler kapatıldı');
    }
  };
  
  // Engellenen kullanıcıları veritabanından yükle
  const loadBlockedUsers = async () => {
    try {
      if (!currentUser?.id) return;
      
      const { data, error } = await supabase
        .from('user_blocks')
        .select('blocked_user_id')
        .eq('user_id', currentUser.id);
        
      if (error) {
        // Tablo henüz yoksa oluştur
        if (error.code === '42P01') { // Tablo yok hatası
          // console.log('user_blocks tablosu bulunamadı, oluşturuluyor...');
          
          // Tablo oluştur
          const { error: createError } = await supabase.rpc('create_user_blocks_table');
          
          if (createError) {
            console.error('Tablo oluşturma hatası:', createError);
            // Hata durumunda localStorage'daki verileri kullan
            return;
          } else {
            // console.log('user_blocks tablosu başarıyla oluşturuldu');
            
            // Mevcut localStorage verilerini veritabanına aktar
            if (blockedUsers.length > 0) {
              const blocksToInsert = blockedUsers.map(userId => ({
                user_id: currentUser.id,
                blocked_user_id: userId,
                blocked_at: new Date().toISOString()
              }));
              
              const { error: insertError } = await supabase
                .from('user_blocks')
                .insert(blocksToInsert);
                
              if (insertError) {
                console.error('Engellenen kullanıcıları aktarma hatası:', insertError);
              } else {
                // console.log('Engellenen kullanıcılar veritabanına aktarıldı');
              }
            }
            
            return; // İlk oluşturma durumunda boş liste döndür
          }
        } else {
          console.error('Engellenen kullanıcılar yüklenemedi:', error);
          return; // Hata durumunda mevcut state'i koru
        }
      }
      
      // Veritabanından gelen verileri state'e ve localStorage'a aktar
      const blockedIds = data.map(block => block.blocked_user_id);
      setBlockedUsers(blockedIds);
      localStorage.setItem('blockedUsers', JSON.stringify(blockedIds));
      // console.log(`${blockedIds.length} engellenen kullanıcı yüklendi`);
    } catch (error) {
      console.error('Engellenen kullanıcılar yüklenirken hata:', error);
    }
  };
  
  // Kullanıcı engelleme/engeli kaldırma
  const toggleBlockUser = async (userId, userName) => {
    if (!currentUser?.id) return;
    
    // Mevcut durumu kontrol et
    const isBlocked = blockedUsers.includes(userId);
    
    try {
      if (isBlocked) {
        // Engeli kaldır (veritabanından)
        const { error } = await supabase
          .from('user_blocks')
          .delete()
          .eq('user_id', currentUser.id)
          .eq('blocked_user_id', userId);
          
        if (error) {
          console.error('Veritabanından engel kaldırma hatası:', error);
          // Veritabanı hatası durumunda sadece UI'ı güncelle
        }
        
        // UI ve localStorage güncelle
        const updatedBlockedUsers = blockedUsers.filter(id => id !== userId);
        setBlockedUsers(updatedBlockedUsers);
        localStorage.setItem('blockedUsers', JSON.stringify(updatedBlockedUsers));
        toast.success(`${userName} kullanıcısının engeli kaldırıldı`);
      } else {
        // Engelle (veritabanına ekle)
        const { error } = await supabase
          .from('user_blocks')
          .insert({
            user_id: currentUser.id,
            blocked_user_id: userId,
            blocked_at: new Date().toISOString()
          });
          
        if (error) {
          console.error('Veritabanına engelleme kaydı hatası:', error);
          // Veritabanı hatası durumunda sadece UI'ı güncelle
        }
        
        // UI ve localStorage güncelle
        const updatedBlockedUsers = [...blockedUsers, userId];
        setBlockedUsers(updatedBlockedUsers);
        localStorage.setItem('blockedUsers', JSON.stringify(updatedBlockedUsers));
        toast.success(`${userName} kullanıcısı engellendi`);
        
        // Eğer aktif sohbet ise kapatılsın
        if (currentChat?.otherUser?.id === userId) {
          setActiveChat(null);
        }
      }
    } catch (error) {
      console.error('Engelleme işlemi sırasında hata:', error);
      toast.error('İşlem başarısız oldu');
    }
  };
  
  // Sohbeti arşivle/arşivden çıkar
  const toggleArchiveChat = (chatId, chatName) => {
    // Mevcut durumu kontrol et
    const isArchived = archivedChats.includes(chatId);
    
    if (isArchived) {
      // Arşivden çıkar
      const updatedArchivedChats = archivedChats.filter(id => id !== chatId);
      setArchivedChats(updatedArchivedChats);
      localStorage.setItem('archivedChats', JSON.stringify(updatedArchivedChats));
      toast.success(`${chatName} sohbeti arşivden çıkarıldı`);
    } else {
      // Arşivle
      const updatedArchivedChats = [...archivedChats, chatId];
      setArchivedChats(updatedArchivedChats);
      localStorage.setItem('archivedChats', JSON.stringify(updatedArchivedChats));
      toast.success(`${chatName} sohbeti arşivlendi`);
    }
  };
  
  // Meşgul durumuna geç
  const setUserBusy = async () => {
    try {
      if (!currentUser?.id) return;
      
      // Meşgul durumunu veritabanında saklamak için bir alan ekleyebilirsiniz
      // Şimdilik sadece bir toast mesajı gösteriyoruz
      toast.success('Durumunuz "Meşgul" olarak ayarlandı');
    } catch (error) {
      console.error('Durum güncellenirken hata:', error);
      toast.error('Durum güncellenemedi');
    }
  };
  
  // Sohbette arama yap
  const searchInChat = (query) => {
    if (!query.trim() || !activeChat) {
      setChatSearchResults([]);
      return;
    }
    
    // Mesajlarda arama yap
    const results = messages.filter(msg => 
      msg.message_text.toLowerCase().includes(query.toLowerCase())
    );
    
    setChatSearchResults(results);
    toast.success(`${results.length} sonuç bulundu`);
  };
  
  // Grup açıklamasını güncelleme işlevini güvenli şekilde çağıran wrapper
  const handleUpdateGroupDescription = (description) => {
    if (currentChat && currentChat.conversation_id) {
      updateGroupDescription(currentChat.conversation_id, description);
    } else if (activeChat) {
      updateGroupDescription(activeChat, description);
    } else {
      toast.error("Grup bilgisi bulunamadı. Lütfen tekrar deneyin.");
    }
  };
  
  // Grup açıklamasını güncelle
  const updateGroupDescription = async (groupId, newDescription) => {
    try {
      // Debug için grupId'yi loglayalım
      // console.log('updateGroupDescription called with:', { groupId, type: typeof groupId });
      
      // Grupid kontrolleri - hem string hem obje olabilir
      let actualGroupId = groupId;
      
      // Eğer bir obje geldiyse (activeChat gibi), içinden ID'yi çıkaralım
      if (typeof groupId === 'object' && groupId !== null) {
        //console.log('GroupId is an object with properties:', Object.keys(groupId));
        actualGroupId = groupId.id || groupId.conversation_id;
        //console.log('Extracted ID:', actualGroupId);
      }
      
      // ID kontrolü yap
      if (!actualGroupId) {
        console.error('Grup ID\'si tanımsız', { original: groupId, actual: actualGroupId });
        
        // Mevcut sohbeti kontrol et - currentChat genellikle daha fazla bilgi içerir
        if (currentChat && currentChat.conversation_id) {
          //console.log('Using currentChat.conversation_id as fallback:', currentChat.conversation_id);
          actualGroupId = currentChat.conversation_id;
        } else if (activeChat && typeof activeChat === 'string') {
          //console.log('Using activeChat as string fallback:', activeChat);
          actualGroupId = activeChat;
        } else {
          toast.error('Grup bilgileri eksik, lütfen tekrar deneyin');
          return;
        }
      }
      
      // Supabase'de conversations tablosunda görünen tüm alanları alalım
      const { data: conversationInfo, error: infoError } = await supabase
        .from('conversations')
        .select('*')
        .eq('id', actualGroupId)
        .single();
      
      if (infoError) {
        console.error('Grup bilgileri alınamadı:', infoError);
        toast.error('Grup bilgileri alınamadı');
        return;
      }
      
      // Metadata alanını kullanalım (JSONB tipindedir ve esnek veri saklamayı sağlar)
      let metadata = conversationInfo.metadata || {};
      metadata = {
        ...metadata,
        description: newDescription
      };
      
      // Açıklamayı metadata alanına kaydedelim
      const { error } = await supabase
        .from('conversations')
        .update({ metadata: metadata })
        .eq('id', actualGroupId);
        
      if (error) throw error;
      
      toast.success('Grup açıklaması güncellendi');
      
      // Konuşmaları güncelle
      fetchConversations();
      setIsEditingGroupDesc(false);
    } catch (error) {
      console.error('Grup açıklaması güncellenirken hata:', error);
      toast.error('Grup açıklaması güncellenemedi');
    }
  };
  
  // Grup profil fotoğrafını güncelle
  const updateGroupPhoto = async (groupId, photoFile) => {
    try {
      if (!photoFile) {
        return;
      }

      if (!groupId) {
        console.error('Grup ID değeri geçersiz');
        toast.error('Grup ID değeri geçersiz');
        return;
      }

      // 1) Mevcut grup fotoğrafının URL'sini al
      // Bunu currentChat ya da veritabanında fetch ettiğiniz conversation objesinden çekebilirsiniz:
      // Örnek: eğer currentChat varsa, currentChat.group_photo_url
      let oldPhotoUrl = null;
      try {
        const { data: conversationData, error: convErr } = await supabase
          .from('conversations')
          .select('group_photo_url')
          .eq('id', groupId)
          .single();
        if (!convErr && conversationData) {
          oldPhotoUrl = conversationData.group_photo_url;
        }
      } catch (err) {
        console.error('Mevcut fotoğraf URL bilgisi alınamadı:', err);
      }

      // 2) Eski fotoğrafı sil (varsa)
      if (oldPhotoUrl) {
        await removeOldFile(oldPhotoUrl);
      }


      
      // 3) Yükleme bildirimi göster
      const toastId = toast.loading('Fotoğraf yükleniyor...');

      // 4) Rastgele dosya adı oluştur
      const fileExt = photoFile.name.split('.').pop();
      const fileName = `group-${groupId}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `group_photos/${fileName}`;

      // 5) Dosyayı chat-avatars bucket'ına yükle
      const { error: uploadError } = await supabase
        .storage
        .from('chat-avatars')
        .upload(filePath, photoFile);

      if (uploadError) throw uploadError;

      // 6) Public URL al
      const { data: { publicUrl } } = supabase
        .storage
        .from('chat-avatars')
        .getPublicUrl(filePath);

      // 7) Veritabanında group_photo_url'i güncelle
      const { error: updateError } = await supabase
        .from('conversations')
        .update({ group_photo_url: publicUrl })
        .eq('id', groupId);

      if (updateError) throw updateError;

      // 8) Bildirim göster
      toast.dismiss(toastId);
      toast.success('Grup fotoğrafı güncellendi');

      // 9) Konuşmaları güncelle
      await fetchConversations();

      // 10) Modalı kapat ve state'i temizle
      setIsChangingGroupPhoto(false);
      setGroupPhoto(null);
      setGroupPhotoPreview(null);

      return publicUrl;
    } catch (error) {
      console.error('Grup fotoğrafı güncellenirken hata:', error);
      toast.error('Grup fotoğrafı güncellenemedi: ' + error.message);
      return null;
    }
  };

  
  // Üye çıkar
  const removeMemberFromGroup = async (groupId, memberId, memberName) => {
    try {
      // Önce kullanıcının yönetici olup olmadığını kontrol et
      const { data: conversation, error: fetchError } = await supabase
        .from('conversations')
        .select('members')
        .eq('conversation_id', groupId)
        .single();
        
      if (fetchError) throw fetchError;
      
      // Mevcut kullanıcı bilgisini al
      const currentUserMember = conversation.members.find(m => m.id === currentUser.id);
      
      // Sadece yöneticiler üye çıkarabilir
      if (!currentUserMember || currentUserMember.role !== 'admin') {
        toast.error('Sadece grup yöneticileri üye çıkarabilir');
        return;
      }
      
      // Kullanıcı kendini çıkaramaz (bunun için gruptan ayrıl kullanılmalı)
      if (memberId === currentUser.id) {
        toast.error('Kendinizi gruptan çıkaramazsınız. Gruptan ayrılmak için "Gruptan Ayrıl" seçeneğini kullanın.');
        return;
      }
      
      // Üyeyi gruptan çıkar
      const updatedMembers = conversation.members.filter(member => member.id !== memberId);
      
      // Güncelleme
      const { error: updateError } = await supabase
        .from('conversations')
        .update({ members: updatedMembers })
        .eq('conversation_id', groupId);
        
      if (updateError) throw updateError;
      
      toast.success(`${memberName} gruptan çıkarıldı`);
      
      // Konuşmaları güncelle
      fetchConversations();
    } catch (error) {
      console.error('Üye çıkarılırken hata:', error);
      toast.error('Üye çıkarılamadı');
    }
  };
  
  // Üye rolünü güncelle (admin/member)
  const updateMemberRole = async (groupId, memberId, memberName, newRole) => {
    try {
      // Önce kullanıcının yönetici olup olmadığını kontrol et
      const { data: conversation, error: fetchError } = await supabase
        .from('conversations')
        .select('members')
        .eq('conversation_id', groupId)
        .single();
        
      if (fetchError) throw fetchError;
      
      // Mevcut kullanıcı bilgisini al
      const currentUserMember = conversation.members.find(m => m.id === currentUser.id);
      
      // Sadece yöneticiler rol değiştirebilir
      if (!currentUserMember || currentUserMember.role !== 'admin') {
        toast.error('Sadece grup yöneticileri rol değiştirebilir');
        return;
      }
      
      // En az bir yönetici olması gerekir, eğer son yöneticiyi normal üye yapmaya çalışıyorsa engelle
      if (newRole !== 'admin') {
        const adminCount = conversation.members.filter(m => m.role === 'admin').length;
        if (adminCount === 1 && currentUserMember.id === memberId) {
          toast.error('Grupta en az bir yönetici olmalıdır');
          return;
        }
      }
      
      // Üyenin rolünü güncelle
      const updatedMembers = conversation.members.map(member => {
        if (member.id === memberId) {
          return {
            ...member,
            role: newRole
          };
        }
        return member;
      });
      
      // Güncelleme
      const { error: updateError } = await supabase
        .from('conversations')
        .update({ members: updatedMembers })
        .eq('conversation_id', groupId);
        
      if (updateError) throw updateError;
      
      toast.success(`${memberName} artık ${newRole === 'admin' ? 'yönetici' : 'normal üye'}`);
      
      // Konuşmaları güncelle
      fetchConversations();
    } catch (error) {
      console.error('Üye rolü güncellenirken hata:', error);
      toast.error('Üye rolü güncellenemedi');
    }
  };

  // Dropdown dışına tıklandığında dropdown menüyü kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (headerDropdownRef.current && !headerDropdownRef.current.contains(event.target)) {
        setShowHeaderDropdown(false);
      }
    };

    // Olay dinleyicisini ekle
    document.addEventListener("mousedown", handleClickOutside);
    
    // Temizleme fonksiyonu
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [headerDropdownRef]);

  // Tüm konuşma için mesajları toplu olarak okundu olarak işaretler
  const markAllMessagesAsRead = async (conversationId) => {
    try {
      if (!currentUser?.id || !conversationId) return;
      
      // Olası fonksiyon isimlerini deneyelim
      const possibleFunctionNames = [
        'mark_conversation_messages_as_read',
        'mark_conversation_as_read',
        'mark_messages_as_read'
      ];
      
      let success = false;
      
      // Fonksiyonları sırayla deneyelim
      for (const funcName of possibleFunctionNames) {
        try {
          const { data, error } = await supabase.rpc(funcName, {
            p_conversation_id: conversationId,
            p_user_id: currentUser.id
          });
          
          if (!error) {
            // console.log(`"${funcName}" başarıyla çağrıldı, ${data} mesaj okundu olarak işaretlendi`);
            success = true;
            break; // Başarılı olduysa döngüden çık
          }
        } catch (functionError) {
          // console.warn(`"${funcName}" fonksiyonu bulunamadı veya çalıştırılamadı:`, functionError);
          // Sessizce diğer fonksiyonu dene
        }
      }
      
      // Eğer RPC fonksiyonlarından hiçbiri çalışmadıysa, eski yöntemi kullan
      if (!success) {
        //console.log('Hiçbir RPC fonksiyonu bulunamadı, alternatif yöntem kullanılıyor...');
        await markMessagesAsReadLegacy(conversationId);
      }
      
      return true;
    } catch (error) {
      console.error('Toplu mesaj okuma işlemi hatası:', error);
      return false;
    }
  };
  
  // Eski yöntemle mesajları okundu olarak işaretler (yedek)
  const markMessagesAsReadLegacy = async (conversationId) => {
    try {
      // 1. Konuşmadaki karşı taraftan gelen tüm okunmamış mesajları al
      const { data: unreadMessages, error: fetchError } = await supabase
        .from('messages')
        .select('id')
        .eq('conversation_id', conversationId)
        .eq('is_read', false)
        .neq('sender_id', currentUser.id);
      
      if (fetchError) {
        console.error('Okunmamış mesajlar alınırken hata:', fetchError.message || fetchError);
        return false;
      }
      
      if (!unreadMessages || unreadMessages.length === 0) {
        // console.log('Okunmamış mesaj bulunmuyor');
        return true;
      }
      
      const messageIds = unreadMessages.map(msg => msg.id);
      
      // 2. Mesajları toplu olarak okundu olarak işaretle
      try {
        const { error: updateError } = await supabase
          .from('messages')
          .update({ is_read: true })
          .in('id', messageIds);
        
        if (updateError) {
          console.error('Mesajlar okundu olarak işaretlenirken hata:', updateError.message || updateError);
        }
      } catch (updateErr) {
        console.error('Mesaj güncelleme hatası:', updateErr.message || updateErr);
      }
      
      // 3. message_status tablosuna toplu kayıt eklemeyi dene
      try {
        const statusRecords = messageIds.map(msgId => ({
          message_id: msgId,
          user_id: currentUser.id,
          is_read: true,
          read_at: new Date().toISOString()
        }));
        
        // Veritabanı yapısına göre UPSERT veya INSERT
        const { error: insertError } = await supabase
          .from('message_status')
          .upsert(statusRecords, { 
            onConflict: 'message_id,user_id',
            ignoreDuplicates: false
          });
        
        if (insertError) {
          console.error('Mesaj durumları eklenirken hata:', insertError.message || insertError);
          
          // Eğer UPSERT hata verirse, tek tek INSERT dene
          for (const record of statusRecords) {
            try {
              await supabase.from('message_status').insert(record);
            } catch (singleError) {
              // Sessizce devam et
            }
          }
        }
      } catch (statusErr) {
        console.error('Mesaj durumu güncelleme hatası:', statusErr.message || statusErr);
      }
      
      // En azından UI'da mesajları okundu olarak işaretle
      setMessages(prevMessages => prevMessages.map(msg => 
        messageIds.includes(msg.message_id) 
          ? { ...msg, is_read: true }
          : msg
      ));
      
      return true;
    } catch (error) {
      console.error('Toplu mesaj okuma işlemi hatası (yedek yöntem):', error.message || error);
      return false;
    }
  };

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // location.state?.startChat kontrolü
    if (!location.state?.startChat) return;
    
    const { userId, userName } = location.state;
    if (!userId || !userName) {
      console.error('Sohbet başlatılamadı: Eksik user ID veya user name');
      toast.error('Sohbet başlatma için gerekli bilgiler eksik');
      navigate('/chat', { replace: true, state: {} });
      return;
    }
    
    // Eğer currentUser henüz yüklenmediyse fonksiyonu çalıştırma
    if (!currentUser) {
      //console.log('Kullanıcı bilgileri henüz yüklenmedi, bekleniyor...');
      return; // currentUser değiştiğinde bu useEffect tekrar çalışacak
    }
    
    // Eğer kullanıcı kendisine mesaj göndermek istiyorsa engelle
    if (userId === currentUser.id) {
      toast.error('Kendinize mesaj gönderemezsiniz');
      navigate('/chat', { replace: true, state: {} });
      return;
    }
    
    // Otomatik olarak chat başlat
    const startChatWithUser = async () => {
      let toastId = null;
      try {
        // Loading toast'u göster
        toastId = toast.loading(`${userName} ile sohbet başlatılıyor...`);
        
        //console.log(`${userName} (${userId}) ile sohbet başlatılıyor...`);
        //console.log('Mevcut kullanıcı:', currentUser.id);
        
        // Sohbeti başlatmayı dene
        const conversationId = await startPrivateChat(userId);
        
        if (!conversationId) {
          throw new Error('Sohbet ID oluşturulamadı');
        }
        
        //console.log(`Sohbet başarıyla başlatıldı. Conversation ID: ${conversationId}`);
        
        // Loading toast'u kapat
        toast.dismiss(toastId);
        
        // Başarı mesajı göster
        toast.success(`${userName} ile sohbet başlatıldı`);
        
        // Sohbeti aktif et
        setActiveChat(conversationId);
        
        // Konuşmaları tekrar yükle
        const newConversations = await fetchConversations();
        if (newConversations) {
          setConversations(newConversations);
        }
      } catch (error) {
        // Loading toast'u kapat
        if (toastId) toast.dismiss(toastId);
        
        // Hata detaylarını logla
        console.error('Sohbet başlatma hatası:', error);
        
        // Kullanıcıya uygun hata mesajı göster
        toast.error(`Sohbet başlatılamadı: ${error.message || 'Bağlantı hatası'}`);
        
        // Hata analizini logla
        if (error.details) console.error('Hata detayları:', error.details);
      }
    };
    
    // Chat başlatma fonksiyonunu çağır
    startChatWithUser();
    
    // State'i temizle (fonksiyonun tekrar çalışmasını önlemek için)
    navigate('/chat', { replace: true, state: {} });
    
  }, [location.state, currentUser, navigate]);

  // Dosya yükleme işlevi - DEVRE DIŞI BIRAKILDI
  const handleAttachmentUpload = (e) => {
    // Seçilen dosyayı temizle
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    
    // Dosya yükleme özelliği devre dışı olduğunu belirt
    toast.error('Dosya yükleme hizmetimiz şu anda devre dışıdır. Lütfen daha sonra tekrar deneyiniz.');
  };
  
  // Dosya gönderme işlevi - DEVRE DIŞI BIRAKILDI 
  const handleSendFile = async () => {
    // Dosya yükleme özelliği devre dışı olduğunu belirt
    toast.error('Dosya yükleme hizmetimiz şu anda devre dışıdır. Lütfen daha sonra tekrar deneyiniz.');
    
    // Temizle
    setSelectedFile(null);
    setFilePreview(null);
    setShowFilePreview(false);
    
    // İnput'u temizle
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Mesaj içeriğini render et - dosya, görsel gibi farklı içerikleri ele al
  const renderMessageContent = (msg) => {
    if (!msg) return null;
    
    const messageType = msg.message_type || 'text';
    
    // Debug: Eklenti kontrolü
    if (['image', 'file'].includes(messageType) && !msg.attachment) {
      // console.warn(`Eksik eklenti bilgisi, mesaj ID: ${msg.id}, mesaj tipi: ${messageType}`);
    }
    
    // Dosya URL'ini alma fonksiyonu - Birden fazla URL alternatifi kontrol eder
    const getFileUrl = (attachment) => {
      if (!attachment) return null;
      
      /*console.log('Dosya bilgileri:', { 
        id: attachment.id,
        messageId: attachment.message_id, 
        fileName: attachment.file_name,
        fileType: attachment.file_type
      });*/
      
      // 1. Önce ana dosya URL'ini kontrol edelim
      if (attachment.file_url && isValidUrl(attachment.file_url)) {
        //console.log('Dosya URL kullanılıyor:', attachment.file_url);
        return attachment.file_url;
      }
      
      // 2. Public URL'i kontrol edelim
      if (attachment.public_url && isValidUrl(attachment.public_url)) {
        //console.log('Public URL kullanılıyor:', attachment.public_url);
        return attachment.public_url;
      }
      
      // 3. JavaScript özel alanındaki URL'i kontrol edelim
      if (attachment._publicUrl && isValidUrl(attachment._publicUrl)) {
        //console.log('Özel URL (_publicUrl) kullanılıyor:', attachment._publicUrl);
        return attachment._publicUrl;
      }
      
      // 4. Thumbnail URL'ini kontrol edelim
      if (attachment.thumbnail_url && isValidUrl(attachment.thumbnail_url)) {
        //console.log('Thumbnail URL kullanılıyor:', attachment.thumbnail_url);
        return attachment.thumbnail_url;
      }
      
      // 5. Hiçbir URL bulunamadıysa, yeni bir URL oluştur
      try {
        const baseUrl = import.meta.env.VITE_SUPABASE_URL;
        const bucketName = 'chat_media';
        
        if (attachment.message_id && activeChat) {
          // En doğru URL formatı: conversationId/messageId/dosyaAdı
          const fileName = `${activeChat}/${attachment.message_id}/${attachment.file_name}`;
          const publicUrl = `${baseUrl}/storage/v1/object/public/${bucketName}/${fileName}`;
          
          //console.log('Oluşturulan URL:', publicUrl);
          return publicUrl;
        }
      } catch (err) {
        console.error('URL oluşturma hatası:', err);
      }
      
      console.error('Dosya URL\'si bulunamadı:', attachment);
      return null;
    };
    
    // URL'nin geçerli olup olmadığını kontrol et
    const isValidUrl = (url) => {
      if (!url) return false;
      try {
        const urlObj = new URL(url);
        return urlObj.protocol.startsWith('http');
      } catch (e) {
        console.warn('Geçersiz URL formatı:', url);
        return false;
      }
    };
    
    // Resim yükleme hatasını işleme fonksiyonu
    const handleImageError = (e, fileName) => {
      console.error(`Resim yüklenemedi: ${fileName || 'isimsiz dosya'}`);
      e.target.src = 'https://via.placeholder.com/300x200?text=Resim+Yüklenemedi';
      e.target.classList.add('error-image');
      e.target.classList.add('opacity-70');
      
      // Error badge
      const errorBadge = document.createElement('div');
      errorBadge.className = 'absolute top-0 right-0 bg-red-500 text-white text-xs px-2 py-1 rounded-bl-lg rounded-tr-lg';
      errorBadge.innerText = 'Hata';
      e.target.parentNode.style.position = 'relative';
      e.target.parentNode.appendChild(errorBadge);
    };
    
    switch (messageType) {
      case 'image':
        // Resim mesajı
        return (
          <div className="message-image">
            {msg.attachment && getFileUrl(msg.attachment) ? (
              <div className="relative">
                <img
                  src={getFileUrl(msg.attachment)}
                  alt={msg.attachment.file_name || "Resim"}
                  className="rounded-lg max-w-full max-h-[300px] object-contain cursor-pointer hover:opacity-90 transition-opacity"
                  onClick={() => window.open(getFileUrl(msg.attachment), '_blank')}
                  onError={(e) => handleImageError(e, msg.attachment.file_name)}
                  loading="lazy"
                />
                <div className="text-xs mt-1 text-center">
                  {msg.attachment.file_name}
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-20 w-full rounded-lg bg-gray-200 dark:bg-gray-700">
                <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                  Resim yüklenemedi - Hatalı eklenti bilgisi
                </span>
                <button 
                  className="mt-2 text-xs px-2 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded"
                  onClick={() => toast.error('Dosya erişilemez durumda veya silinmiş olabilir.')}
                >
                  Bilgi
                </button>
              </div>
            )}
          </div>
        );
      
      case 'system':
        // Sistem mesajları (grup oluşturma, üye ekleme vb.)
        return (
          <div className="py-2 px-3 rounded-lg text-center bg-transparent text-gray-600 dark:text-gray-300 text-sm italic">

            {msg.message_text}
          </div>
        );
      
      case 'file':
        // Dosya ve belge mesajları (PDF, Word, Text, Excel, vb.)
        // Belge türünü dosya tipinden tespit et
        if (!msg.attachment) {
          console.error('Dosya eklentisi bulunamadı, mesaj ID:', msg.id);
          return (
            <div className="flex flex-col items-center gap-2 p-3 rounded-lg bg-gray-200 dark:bg-gray-700">
              <div className="flex items-center">
                <FileText className="w-5 h-5 text-gray-400 mr-2" />
                <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                  Dosya yüklenemedi - Eklenti bilgisi eksik
                </span>
              </div>
              <div className="flex gap-2">
                <button 
                  className="mt-1 text-xs px-2 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded"
                  onClick={() => toast.error('Dosya bilgileri bulunamadı veya silinmiş olabilir.')}
                >
                  Bilgi
                </button>
                <button 
                  className="mt-1 text-xs px-2 py-1 bg-green-500 hover:bg-green-600 text-white rounded"
                  onClick={() => window.location.reload()}
                >
                  Sayfayı Yenile
                </button>
              </div>
            </div>
          );
        }
        
        const fileUrl = getFileUrl(msg.attachment);
        if (!fileUrl) {
          return (
            <div className="flex flex-col items-center gap-2 p-3 rounded-lg bg-gray-200 dark:bg-gray-700">
              <div className="flex items-center">
                <FileText className="w-5 h-5 text-gray-400 mr-2" />
                <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                  Dosya URL'i bulunamadı veya geçersiz
                </span>
              </div>
              <div className="flex gap-2">
                <button 
                  className="mt-1 text-xs px-2 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded"
                  onClick={() => {
                    // Supabase bucket ayarlarını kontrol etme tavsiyesi
                    toast.error(
                      <div>
                        <p>Dosyaya erişim hatası!</p>
                        <p className="text-xs mt-1">Supabase Storage bucket ayarlarını kontrol edin.</p>
                        <p className="text-xs">Bucket'ın herkese açık (public) olduğundan emin olun.</p>
                      </div>
                    );
                  }}
                >
                  Hatayı Göster
                </button>
                <button 
                  className="mt-1 text-xs px-2 py-1 bg-green-500 hover:bg-green-600 text-white rounded"
                  onClick={() => window.location.reload()}
                >
                  Sayfayı Yenile
                </button>
              </div>
            </div>
          );
        }
        
        // İyileştirilmiş dosya türü kontrolü
        const getFileTypeInfo = (attachment) => {
          const fileType = attachment.file_type || '';
          const fileName = attachment.file_name || '';
          const ext = fileName.split('.').pop().toLowerCase();
          
          if (fileType.includes('pdf') || ext === 'pdf') {
            return { icon: <FileText className="w-6 h-6 text-red-500" />, bgColor: 'bg-red-100', name: 'PDF' };
          }
          if (fileType.includes('word') || ['doc', 'docx'].includes(ext)) {
            return { icon: <FileText className="w-6 h-6 text-blue-600" />, bgColor: 'bg-blue-100', name: 'Word' };
          }
          if (fileType.includes('text') || ['txt', 'rtf'].includes(ext)) {
            return { icon: <FileText className="w-6 h-6 text-gray-600" />, bgColor: 'bg-gray-100', name: 'Metin' };
          }
          if (fileType.includes('excel') || fileType.includes('spreadsheet') || ['xls', 'xlsx', 'csv'].includes(ext)) {
            return { icon: <FileText className="w-6 h-6 text-green-600" />, bgColor: 'bg-green-100', name: 'Excel' };
          }
          if (fileType.includes('powerpoint') || fileType.includes('presentation') || ['ppt', 'pptx'].includes(ext)) {
            return { icon: <FileText className="w-6 h-6 text-orange-600" />, bgColor: 'bg-orange-100', name: 'Sunum' };
          }
          
          // Varsayılan
          return { icon: <FileText className="w-6 h-6 text-blue-500" />, bgColor: 'bg-blue-100', name: 'Dosya' };
        };
        
        const fileTypeInfo = getFileTypeInfo(msg.attachment);
        const isDocument = msg.attachment && (
          msg.attachment.file_type?.includes('pdf') || 
          msg.attachment.file_type?.includes('word') || 
          msg.attachment.file_type?.includes('text') || 
          msg.attachment.file_type?.includes('spreadsheet') || 
          msg.attachment.file_type?.includes('excel') || 
          msg.attachment.file_type?.includes('presentation') || 
          msg.attachment.file_type?.includes('powerpoint')
        );
        
        if (isDocument) {
          // Belge render et
          return (
            <div className="message-document">
              <div className={`p-3 rounded-lg ${
                isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
              } hover:opacity-90 transition-all`}>
                <a 
                  href={fileUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-3"
                >
                  {/* Belge türüne göre ikon */}
                  <div className={`flex-shrink-0 w-10 h-10 ${fileTypeInfo.bgColor} rounded-lg flex items-center justify-center ${isDark ? 'opacity-80' : ''}`}>
                    {fileTypeInfo.icon}
                  </div>
                  
                  <div className="overflow-hidden">
                    <div className="text-sm font-medium truncate max-w-[200px]">
                      {msg.attachment.file_name}
                    </div>
                    <div className="text-xs opacity-70 flex items-center gap-2">
                      <span>{(msg.attachment.file_size / 1024).toFixed(1)} KB</span>
                      <span>•</span>
                      <span>Görüntülemek için tıklayın</span>
                    </div>
                  </div>
                </a>
              </div>
            </div>
          );
        } else {
          // Normal dosya mesajı
          return (
            <div className="message-file">
              <div className={`p-3 rounded-lg ${
                isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
              } hover:opacity-90 transition-all`}>
                <a 
                  href={fileUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2"
                >
                  <div className={`flex-shrink-0 w-10 h-10 ${fileTypeInfo.bgColor} rounded-lg flex items-center justify-center ${isDark ? 'opacity-80' : ''}`}>
                    {fileTypeInfo.icon}
                  </div>
                  <div className="overflow-hidden">
                    <div className="text-sm font-medium truncate max-w-[200px]">
                      {msg.attachment.file_name}
                    </div>
                    <div className="text-xs opacity-70 flex items-center gap-2">
                      <span>{(msg.attachment.file_size / 1024).toFixed(1)} KB</span>
                      <span>•</span>
                      <span>İndirmek için tıklayın</span>
                    </div>
                  </div>
                </a>
              </div>
            </div>
          );
        }
      case 'text':
      default:
        // Normal metin mesajı
        return (
          <p className={`whitespace-pre-wrap break-words ${
            localStorage.getItem('messageFontSize') === 'Küçük'
              ? 'text-xs'
              : localStorage.getItem('messageFontSize') === 'Büyük'
                ? 'text-base'
                : 'text-sm' // Varsayılan (Orta)
          }`}>
            {msg.message_text}
          </p>
        );
    }
  };

  // Dosya yüklemeyi iptal et
  const cancelFileUpload = () => {
    setSelectedFile(null);
    setFilePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Dosya yüklemeyi onayla ve gönder
  const confirmFileUpload = async () => {
    if (!selectedFile || !activeChat) return;
    
    const loadingToastId = toast.loading(`"${selectedFile.name}" yükleniyor... (0%)`);
    let uploadProgress = 0;
    let progressInterval;
    
    try {
      // İlerleme gösterimini güncelleyen bir zaman aşımı başlat
      progressInterval = setInterval(() => {
        if (uploadProgress < 90) {
          uploadProgress += Math.floor(Math.random() * 10) + 1;
          uploadProgress = Math.min(uploadProgress, 90); // 90% ile sınırla, son 10% server işlemleri için
          toast.loading(`"${selectedFile.name}" yükleniyor... (${uploadProgress}%)`, { id: loadingToastId });
        }
      }, 800);
      
      // Dosyayı gönder
      const messageData = await sendAttachment(activeChat, selectedFile);
      
      // İlerleme güncelleme zamanlayıcısını temizle
      clearInterval(progressInterval);
      
      if (messageData) {
        // Yükleme tamamlandı bildirimi
        toast.success(`"${selectedFile.name}" başarıyla gönderildi`, { id: loadingToastId });
        
        // Mesajı zenginleştir ve mesajlar listesine ekle
        const enrichedMessage = await enrichMessageData(messageData);
        setMessages(prev => [...prev, enrichedMessage]);
        
        // Mesaj alanını temizle
        setMessage('');
        
        // Önizlemeyi temizle
        setSelectedFile(null);
        setFilePreview(null);
        
        // Mesaj listesinin sonuna kaydır
        setTimeout(() => {
          messageEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    } catch (error) {
      console.error('Dosya yükleme hatası:', error);
      
      // İlerleme güncelleme zamanlayıcısını temizle
      if (progressInterval) clearInterval(progressInterval);
      
      toast.error('Dosya yüklenirken bir hata oluştu. Lütfen tekrar deneyin.', { id: loadingToastId });
      
      // Hata durumunda yeniden deneme butonu göster
      setTimeout(() => {
        toast((t) => (
          <div>
            <p>"{selectedFile.name}" yüklenemedi. Tekrar denemek ister misiniz?</p>
            <div className="mt-2 flex justify-end gap-2">
              <button 
                onClick={() => {
                  toast.dismiss(t.id);
                  confirmFileUpload(); // Yüklemeyi tekrar dene
                }}
                className="px-2 py-1 bg-blue-500 text-white rounded text-xs"
              >
                Tekrar Dene
              </button>
              <button 
                onClick={() => {
                  toast.dismiss(t.id);
                  setSelectedFile(null);
                  setFilePreview(null);
                  if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                  }
                }}
                className="px-2 py-1 bg-gray-500 text-white rounded text-xs"
              >
                İptal
              </button>
            </div>
          </div>
        ), { duration: 10000 });
      }, 1000);
    } finally {
      // Input alanını sıfırla (aynı dosyayı tekrar yükleyebilmek için)
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Skeleton loading bileşenleri - UI için
  const ConversationSkeletonLoading = () => {
    return (
      <div className="animate-pulse">
        {[1, 2, 3, 4, 5].map((item) => (
          <div key={item} className={`w-full p-3 flex items-center gap-3 mb-2 ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
            {/* Avatar skeleton */}
            <div className={`w-12 h-12 rounded-full ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}></div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-2">
                {/* Name skeleton */}
                <div className={`h-4 ${isDark ? 'bg-gray-700' : 'bg-gray-200'} rounded w-24`}></div>
                {/* Time skeleton */}
                <div className={`h-3 ${isDark ? 'bg-gray-700' : 'bg-gray-200'} rounded w-10`}></div>
              </div>
              {/* Message skeleton */}
              <div className={`h-3 ${isDark ? 'bg-gray-700' : 'bg-gray-200'} rounded w-32`}></div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Message Skeleton Component
  const MessageSkeletonLoading = () => {
    // Farklı mesaj türleri için örnekler
    const messageTypes = [
      { type: 'received', length: 'w-52' },
      { type: 'sent', length: 'w-40' },
      { type: 'received', length: 'w-64' },
      { type: 'sent', length: 'w-48' },
      { type: 'received', length: 'w-32' }
    ];
    
    return (
      <div className="animate-pulse">
        {messageTypes.map((msg, index) => (
          <div key={index} className={`flex mb-4 ${msg.type === 'sent' ? 'justify-end' : 'justify-start'}`}>
            {/* Receiver avatar for received messages */}
            {msg.type === 'received' && (
              <div className={`w-8 h-8 rounded-full mr-2 ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}></div>
            )}
            
            <div className={`${msg.length} max-w-[70%]`}>
              {/* Sender name for received messages */}
              {msg.type === 'received' && (
                <div className={`h-3 ${isDark ? 'bg-gray-700' : 'bg-gray-200'} rounded w-20 mb-2`}></div>
              )}
              
              {/* Message bubble */}
              <div className={`h-8 rounded-lg ${
                msg.type === 'sent' 
                  ? (isDark ? 'bg-blue-600' : 'bg-blue-400') 
                  : (isDark ? 'bg-gray-700' : 'bg-gray-200')
              }`}></div>
              
              {/* Time */}
              <div className={`h-2 ${isDark ? 'bg-gray-700' : 'bg-gray-200'} rounded w-10 mt-1 ${
                msg.type === 'sent' ? 'ml-auto' : 'mr-auto'
              }`}></div>
            </div>
            
            {/* Spacer for sent messages */}
            {msg.type === 'sent' && (
              <div className="w-8 ml-2"></div>
            )}
          </div>
        ))}
      </div>
    );
  };

  // Grup profil fotoğrafını seç
  const handleGroupPhotoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Dosya boyutunu kontrol et (5MB'dan büyük olmamalı)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Dosya boyutu 5MB\'dan küçük olmalıdır');
        return;
      }
      
      // Dosya tipini kontrol et (sadece resim olmalı)
      if (!file.type.startsWith('image/')) {
        toast.error('Lütfen sadece resim dosyası yükleyin');
        return;
      }
      
      setGroupPhoto(file);
      
      // Önizleme için URL oluştur
      const reader = new FileReader();
      reader.onload = () => {
        setGroupPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
// Grup fotoğrafını kaldır
const removeGroupPhoto = async () => {
  try {
    // currentChat ya da aktif grubun ID'sini edinin
    const groupId = currentChat?.id;
    if (!groupId) {
      console.error('Grup ID değeri bulunamadı');
      toast.error('Grup ID değeri bulunamadı');
      return;
    }

    // 1) Mevcut fotoğraf URL'sini çek
    let oldPhotoUrl = null;
    try {
      const { data: conversationData, error: convErr } = await supabase
        .from('conversations')
        .select('group_photo_url')
        .eq('id', groupId)
        .single();
      if (!convErr && conversationData) {
        oldPhotoUrl = conversationData.group_photo_url;
      }
    } catch (err) {
      console.error('Mevcut fotoğraf URL bilgisi alınamadı:', err);
    }

    // 2) Eğer eski URL varsa storage'tan sil
    if (oldPhotoUrl) {
      await removeOldFile(oldPhotoUrl);
    }

    // 3) Veritabanında group_photo_url: null olarak güncelle
    const toastId = toast.loading('Fotoğraf kaldırılıyor...');
    const { error: updateError } = await supabase
      .from('conversations')
      .update({ group_photo_url: null })
      .eq('id', groupId);

    if (updateError) throw updateError;

    toast.dismiss(toastId);
    toast.success('Grup fotoğrafı kaldırıldı');

    // 4) State'leri temizle
    setGroupPhoto(null);
    setGroupPhotoPreview(null);

    // 5) Konuşmaları güncelle
    await fetchConversations();

    // 6) Modal kapat
    setIsChangingGroupPhoto(false);

  } catch (error) {
    console.error('Grup fotoğrafı kaldırılırken hata:', error);
    toast.error('Grup fotoğrafı kaldırılamadı: ' + error.message);
  }
};


  // Mevcut mesajları okundu olarak işaretle
  useEffect(() => {
    if (activeChat && messages && messages.length > 0) {
      // Toplu işaretleme yerine tüm mesajları tek tek işaretliyoruz
      markMessagesAsReadLegacy(activeChat);
    }
  }, [activeChat, messages]);
  
  // Sayfa yüklendiğinde otomatik kaydırma için
  useEffect(() => {
    if (messageEndRef.current) {
      messageEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Mesaj container scroll olayını izle
  useEffect(() => {
    const handleScroll = () => {
      const container = messageContainerRef.current;
      if (container) {
        // Sayfa dibinden mesafe (100px içindeyse dibteyiz sayılır)
        const isBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
        
        setIsNearBottom(isBottom);
        
        // Eğer dibindeyse, "Dibe git" butonunu gizle ve okunmamış mesajları temizle
        if (isBottom) {
          setShowScrollBottom(false);
          setUnreadMessages([]);
        } else {
          setShowScrollBottom(true);
        }
      }
    };

    const container = messageContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => {
        container.removeEventListener('scroll', handleScroll);
      };
    }
  }, []);

  // Yeni mesaj geldiğinde çalışacak fonksiyon
  useEffect(() => {
    // Eğer sayfa dibinde değilsek, son gelen mesajı unreadMessages listesine ekle
    if (messages.length > 0 && !isNearBottom) {
      const lastMessage = messages[messages.length - 1];
      
      // Kendi mesajımız değilse ve daha önce eklenmemişse
      if (lastMessage.sender_id !== currentUser?.id && 
          !unreadMessages.some(msg => msg.id === lastMessage.id || msg.message_id === lastMessage.message_id)) {
        
        setUnreadMessages(prev => {
          // En fazla son 3 mesajı gösteriyoruz
          const newMessages = [...prev, lastMessage].slice(-3);
          return newMessages;
        });
      }
    }
  }, [messages, isNearBottom, currentUser?.id, unreadMessages]);

  // Dibe kaydırma fonksiyonu
  const scrollToBottom = () => {
    const container = messageContainerRef.current;
    if (container) {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth'
      });
    }
    // Okunmamış mesajları temizle
    setUnreadMessages([]);
  };

  // Message değiştiğinde textarea yüksekliğini otomatik ayarla
  useEffect(() => {
    const textarea = document.querySelector('.chat-textarea');
    if (textarea) {
      // Eğer mesaj boşsa, textarea'yı temel yüksekliğe ayarla
      if (!message) {
        textarea.style.height = '24px';
      } else {
        // Önce yüksekliği sıfırla
        textarea.style.height = 'auto';
        // scrollHeight'a göre ayarla, max 120px
        textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
      }
    }
  }, [message]);
  
  return (
    <div className="h-[calc(100vh-5rem)] flex relative overflow-hidden">
      {/* Conversation Sidebar */}
      <AnimatePresence mode="wait">
        {isSidebarOpen && (
          <motion.div 
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 320, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className={`flex-shrink-0 border-r ${
              isDark ? 'border-gray-700' : 'border-gray-200'
            } h-full flex flex-col`}
          >
            {/* Conversation Header */}
            <div className={`p-4 flex items-center justify-between border-b ${
              isDark ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <h2 className={`text-lg font-semibold ${
                isDark ? 'text-white' : 'text-gray-900'
              }`}>
                Sohbetler
              </h2>
              <div className="flex items-center gap-2">
                <button 
                  onClick={async () => {
                    try {
                      // Yükleniyor bildirimini göster
                      const toastId = toast.loading('Sohbetler yenileniyor...');
                      
                      // Sohbetleri yeniden yükle
                      setConversationsLoading(true);
                      const freshConversations = await fetchConversations();
                      
                      if (freshConversations) {
                        setConversations(freshConversations);
                        // Başarılı bildirim göster
      toast.dismiss(toastId);
                        toast.success('Sohbetler yenilendi');
                      } else {
                        // Hata durumunda bildirim
                        toast.dismiss(toastId);
                        toast.error('Sohbetler yenilenemedi');
                      }
                    } catch (error) {
                      console.error('Sohbetleri yenilerken hata:', error);
                      toast.error('Sohbetler yenilenemedi: ' + error.message);
                    } finally {
                      setConversationsLoading(false);
                    }
                  }}
                  className={`p-2 rounded-full ${
                    isDark 
                      ? 'hover:bg-gray-700 text-gray-300' 
                      : 'hover:bg-gray-100 text-gray-600'
                  } transition-colors relative group`}
                  title="Sohbetleri Yenile"
                >
                  <RefreshCw className="w-5 h-5" />
                  <span 
                    className={`absolute top-26 mt-4 left-1/2 transform -translate-x-1/2 px-2 py-1 text-xs font-medium rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap ${
                      isDark ? 'bg-gray-700 text-white' : 'bg-white text-gray-800 shadow-md'
                    }`}
                  >
                    <p className='text-xs'>Yenile</p>
                  </span>
                </button>
                <button
                  onClick={() => setShowNewChatModal(true)}
                  className={`p-2 rounded-full ${
                    isDark 
                      ? 'hover:bg-gray-700 text-gray-300' 
                      : 'hover:bg-gray-100 text-gray-600'
                  } transition-colors`}
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            {/* Search */}
            <div className={`px-4 py-3 border-b ${
              isDark ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <div className={`flex items-center gap-2 p-2 rounded-lg  rounded-br-[24px] rounded-tr-[10px] ${
                isDark 
                  ? 'bg-gray-800 text-gray-300' 
                  : 'bg-gray-100 text-gray-600'
              }`}>
                <Search className="w-5 h-5" />
                <input
                  type="text"
                  placeholder="Ara..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`bg-transparent border-none outline-none w-full rounded-br-[18px] rounded-bl-[6px] rounded-tl-[6px] rounded-tr-[4px] ${
                    isDark ? 'placeholder-gray-500 text-white' : 'placeholder-gray-400 text-gray-900'
                  }`}
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="p-1 rounded-full hover:bg-gray-700/50"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            
            {/* Filters */}
            <div className="flex items-center justify-between gap-2 mb-4 px-2">
              <div className="flex flex-col w-full space-y-1 mt-4">
                {/* Başlık */}
                <div className="flex items-center justify-between px-2 mb-1">
                  <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Filtreler
                  </span>
                  <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    {filter === 'all' ? 'Tümü' : filter === 'direct' ? 'Kişiler' : 'Gruplar'}
                  </span>
                </div>
                
                {/* Animasyonlu Tab Seçici */}
                <div className="relative bg-gray-100 dark:bg-gray-800 rounded-xl p-1 backdrop-blur-sm bg-opacity-75 shadow-inner">
                  {/* Hareketli Arka Plan Göstergesi */}
                  <motion.div 
                    className={`absolute top-1 bottom-1 rounded-lg z-0 ${
                      isDark ? 'bg-gradient-to-r from-blue-700 to-blue-500 shadow-lg shadow-blue-700/30' : 
                      'bg-gradient-to-r from-blue-50 to-white shadow-md'
                    }`}
                    animate={{
                      left: filter === 'all' ? '0.25rem' : filter === 'direct' ? '33.33%' : '67.99%',

                      width: 'calc(33.33% - 0.5rem)'
                    }}
                    transition={{
                      type: "spring",
                      stiffness: 300,
                      damping: 25
                    }}
                  />
                  
                  {/* Butonlar */}
                  <div className="flex relative z-10">
                    <motion.button
                      onClick={() => setFilter('all')}
                      whileTap={{ scale: 0.95 }}
                      className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center justify-center gap-1.5 ${
                        filter === 'all' 
                          ? (isDark ? 'text-white' : 'text-blue-800')
                          : (isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-800')
                      }`}
                    >
                      <MessageCircle className="w-4 h-4" />
                      <span>Tümü</span>
                    </motion.button>
                     
                    <div className={`h-5 w-px my-auto opacity-50 ${isDark ? 'bg-gray-600' : 'bg-gray-300'}`}></div>
                     
                    <motion.button
                      onClick={() => setFilter('direct')}
                      whileTap={{ scale: 0.95 }}
                      className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center justify-center gap-1.5 ${
                        filter === 'direct' 
                          ? (isDark ? 'text-white' : 'text-blue-800')
                          : (isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-800')
                      }`}
                    >
                      <User className="w-4 h-4" />
                      <span>Kişiler</span>
                    </motion.button>
                     
                    <div className={`h-5 w-px my-auto opacity-50 ${isDark ? 'bg-gray-600' : 'bg-gray-300'}`}></div>
                     
                    <motion.button
                      onClick={() => setFilter('groups')}
                      whileTap={{ scale: 0.95 }}
                      className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center justify-center gap-1.5 ${
                        filter === 'groups' 
                          ? (isDark ? 'text-white' : 'text-blue-800')
                          : (isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-800')
                      }`}
                    >
                      <Users className="w-4 h-4" />
                      <span>Gruplar</span>
                    </motion.button>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Conversation List */}
            <div className="h-[calc(100%-190px)] overflow-x-hidden overflow-y-auto  ">
              {conversationsLoading && (!conversations || conversations.length === 0) ? (
                <ConversationSkeletonLoading />
              ) : conversations && conversations.length > 0 ? (
                // Filtrelenmiş konuşmaları göster
                <AnimatePresence initial={false}>
                  {conversations
                    .filter(chat => {
                      if (filter === 'all') return true;
                      if (filter === 'direct') return !chat.is_group;
                      if (filter === 'groups') return chat.is_group;
                      return true;
                    })
                    .filter(chat => {
                      if (!searchQuery) return true;
                      return chat.title.toLowerCase().includes(searchQuery.toLowerCase());
                    })
                    // Engellenen kullanıcıları filtrele (grup sohbetleri hariç)
                    .filter(chat => {
                      // Grup sohbetlerini her zaman göster
                      if (chat.is_group) return true;
                      // Karşı taraf engellenmiş mi kontrol et
                      return !blockedUsers.includes(chat.otherUser?.id);
                    })
                    .map(chat => (
                      <motion.button
                        key={chat.conversation_id}
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ 
                          opacity: 1, 
                          y: 0,
                          backgroundColor: highlightedChat === chat.conversation_id 
                            ? (isDark ? '#1e40af' : '#dbeafe') 
                            : (activeChat === chat.conversation_id 
                              ? (isDark ? 'rgba(31, 41, 55, 1)' : 'rgba(239, 246, 255, 1)') 
                              : 'transparent')
                        }}
                        exit={{ opacity: 0, height: 0, marginTop: 0, marginBottom: 0, overflow: 'hidden' }}
                        transition={{ 
                          type: "spring", 
                          stiffness: 500, 
                          damping: 30,
                          duration: 0.3 
                        }}
                        layout
                        layoutId={`chat-${chat.conversation_id}`}
                        whileHover={{ x: 3 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => {
                          // Aktif sohbeti ayarla
                          setActiveChat(chat.conversation_id);
                          
                          // Hemen okunmamış mesaj sayısını sıfırla (UI için)
                          setConversations(prevConversations => {
                            return prevConversations.map(conv => {
                              if (conv.conversation_id === chat.conversation_id) {
                                return {
                                  ...conv,
                                  unread_count: 0
                                };
                              }
                              return conv;
                            });
                          });
                          
                          // Veritabanında tüm mesajları okundu olarak işaretle
                          markAllMessagesAsRead(chat.conversation_id);
                        }}
                        className={`w-full p-3 flex items-center gap-3 border-b border-gray-200 dark:border-gray-800 rounded-tl-[12px] rounded-bl-[36px] ${
                          activeChat === chat.conversation_id 
                            ? (isDark ? 'bg-gray-800' : 'bg-blue-50') 
                            : (isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-50')
                        } ${
                          highlightedChat === chat.conversation_id
                            ? (isDark ? 'bg-blue-900 hover:bg-blue-800' : 'bg-blue-100 hover:bg-blue-200')
                            : ''
                        } transition-colors relative`}
                      >
                        {chat.is_group ? (
                          // Grup avatarı
                          <div className="relative">
                            {chat.group_photo_url ? (
                              <img 
                                src={chat.group_photo_url} 
                                alt={chat.title} 
                                className="w-12 h-12 rounded-full object-cover"
                                onError={(e) => {
                                  // Resim yüklenemezse, ikonla değiştir
                                  e.target.style.display = 'none';
                                  e.target.parentElement.innerHTML = `
                                    <div class="w-12 h-12 rounded-full flex items-center justify-center ${
                                      isDark ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-700'
                                    }">
                                      <span class="text-md font-semibold">${getInitials(chat.title)}</span>
                                    </div>
                                  `;
                                }}
                              />
                            ) : (
                              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                                isDark ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-700'
                              }`}>
                                {getInitials(chat.title)}
                              </div>
                            )}
                          </div>
                        ) : chat.otherUser?.avatar ? (
                          // Kullanıcı avatarı (diğer kullanıcının)
                          <div className="relative">
                            <img 
                              src={chat.otherUser.avatar} 
                              alt={chat.otherUser.name} 
                              className="w-12 h-12 rounded-full object-cover"
                            />
                            <div className={`absolute right-0 bottom-0 w-3 h-3 rounded-full border-2 ${
                              isDark ? 'border-gray-800' : 'border-white'
                            } ${chat.otherUser.online ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                          </div>
                        ) : (
                          // Avatar yoksa baş harfler
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center text-md font-semibold ${
                            isDark ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-700'
                          }`}>
                            {getInitials(chat.title)}
                          </div>
                        )}
                        
                        <div className="flex-1 min-w-0 text-left">
                          <div className="flex items-center justify-between mb-1">
                            <h3 className={`text-sm font-medium truncate ${
                              isDark ? 'text-gray-100' : 'text-gray-900'
                            }`}>
                              {chat.title}
                              {chat.is_group && (
                                <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${
                                  isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                                }`}>
                                  {chat.members?.length || 0}
                                </span>
                              )}
                            </h3>
                            <span className={`text-xs ${
                              isDark ? 'text-gray-400' : 'text-gray-500'
                            }`}>
                              {new Date(chat.last_message_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                          <div className="flex items-center justify-between gap-1">
                            <p className={`text-sm truncate max-w-[85%] ${
                              chat.unread_count > 0
                                ? (isDark ? 'text-gray-300' : 'text-gray-700') 
                                : (isDark ? 'text-gray-400' : 'text-gray-500')
                            } ${chat.unread_count > 0 ? 'font-medium' : ''}`}>
                              {chat.last_message_sender_id === currentUser?.id ? (
                                <span>
                                  <span className="text-xs font-medium text-blue-500">(siz)</span> {chat.last_message || 'Henüz mesaj yok'}
                                </span>
                              ) : (
                                chat.last_message || 'Henüz mesaj yok'
                              )}
                            </p>
                            
                            {chat.unread_count > 0 && (
                              <motion.div 
                                initial={{ scale: 0.5, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ type: "spring", stiffness: 500 }}
                                className={`min-w-[20px] h-[20px] px-1.5 rounded-full text-xs font-medium flex items-center justify-center ${
                                  isDark ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'
                                }`}
                              >
                                {chat.unread_count}
                              </motion.div>
                            )}
                          </div>
                        </div>
                      </motion.button>
                    ))}
                </AnimatePresence>
              ) : (
                <div className={`p-6 text-center ${
                  isDark ? 'text-gray-400' : 'text-gray-600 animate-pulse'
                }`}>
                  {searchQuery ? (
                    <p>Sonuç bulunamadı, başka bir arama yapabilirsiniz.</p>
                  ) : (
                    <div>
                      <p className="mb-3">Henüz sohbet yok.</p>
                      <button
                        onClick={() => setShowNewChatModal(true)}
                        className={`inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium ${
                          isDark 
                            ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                            : 'bg-blue-600 hover:bg-blue-700 text-white'
                        }`}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Yeni Sohbet Başlat
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Analiz Verileri Bölümü */}
            {conversations && conversations.length > 0 && (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className={`mt-auto p-3 border-t ${
                  isDark ? 'border-gray-700 bg-gray-850' : 'border-gray-200 bg-gray-50'
                } rounded-b-lg bg-opacity-30 backdrop-blur-md rounded-tl-[24px] rounded-bl-[36px]`}
                    
              >
                <div className="flex flex-col gap-1.5">
                  <h4 className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Sohbet Analizleri
                  </h4>
                  
                  <div className="grid grid-cols-3 gap-2 mb-1.5">
                    {/* Toplam Sohbet Sayısı */}
                    <div className={`p-2 rounded-lg rounded-br-[0px] rounded-tr-[0px] ${
                      isDark ? 'bg-gray-800' : 'bg-white'
                    }`}>
                      <div className="flex flex-col items-center">
                        <span className={`text-lg font-semibold ${
                          isDark ? 'text-blue-400' : 'text-blue-600'
                        }`}>
                          {conversations.length}
                        </span>
                        <span className={`text-[10px] ${
                          isDark ? 'text-gray-500' : 'text-gray-400'
                        }`}>
                          Toplam
                        </span>
                      </div>
                    </div>
                    
                    {/* Grup Sayısı */}
                    <div className={`p-2  ${
                      isDark ? 'bg-gray-800' : 'bg-white'
                    }`}>
                      <div className="flex flex-col items-center">
                        <span className={`text-lg font-semibold ${
                          isDark ? 'text-green-400' : 'text-green-600'
                        }`}>
                          {conversations.filter(chat => chat.is_group).length}
                        </span>
                        <span className={`text-[10px]  ${
                          isDark ? 'text-gray-500' : 'text-gray-400'
                        }`}>
                          Grup
                        </span>
                      </div>
                    </div>
                    
                    {/* Okunmamış Mesaj Sayısı */}
                    <div className={`p-2 rounded-lg  rounded-lg rounded-bl-[0px] rounded-tl-[0px] ${
                      isDark ? 'bg-gray-800' : 'bg-white'
                    }`}>
                      <div className="flex flex-col items-center">
                        <span className={`text-lg font-semibold ${
                          isDark ? 'text-yellow-400' : 'text-yellow-600'
                        }`}>
                          {conversations.reduce((total, chat) => total + (chat.unread_count || 0), 0)}
                        </span>
                        <span className={`text-[10px] ${
                          isDark ? 'text-gray-500' : 'text-gray-400'
                        }`}>
                          Okunmamış
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* En Aktif Sohbetler */}
                  <div className={`p-2 rounded-lg pt-2 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                    <h5 className={`text-[10px] font-medium mb-1 ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      En Son Sohbetler
                    </h5>
                    
                    {conversations.length > 0 && (
                      <div className="flex items-center gap-2">
                        {/* En son mesaj gönderilen sohbet */}
                        {(() => {
                          const mostRecentChat = [...conversations].sort((a, b) => 
                            new Date(b.last_message_at) - new Date(a.last_message_at)
                          )[0];
                          
                          return (
                            <div className="flex items-center gap-1.5 w-full">
                              <div className="relative">
                                {mostRecentChat.is_group && mostRecentChat.group_photo_url ? (
                                  <img 
                                    src={mostRecentChat.group_photo_url} 
                                    alt={mostRecentChat.title}
                                    className="w-6 h-6 rounded-full object-cover border border-gray-700 dark:border-gray-600"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.parentElement.innerHTML = `
                                        <div class="w-6 h-6 rounded-full flex items-center justify-center ${
                                          isDark ? 'bg-gray-700' : 'bg-gray-200'
                                        }">
                                          <span class="text-[8px]">${getInitials(mostRecentChat.title)}</span>
                                        </div>
                                      `;
                                    }}
                                  />
                                ) : (!mostRecentChat.is_group && mostRecentChat.otherUser?.avatar) ? (
                                  <img 
                                    src={mostRecentChat.otherUser.avatar} 
                                    alt={mostRecentChat.title}
                                    className="w-6 h-6 rounded-full object-cover border border-gray-700 dark:border-gray-600"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.parentElement.innerHTML = `
                                        <div class="w-6 h-6 rounded-full flex items-center justify-center ${
                                          isDark ? 'bg-gray-700' : 'bg-gray-200'
                                        }">
                                          <span class="text-[8px]">${getInitials(mostRecentChat.title)}</span>
                                        </div>
                                      `;
                                    }}
                                  />
                                ) : (
                                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                                    isDark ? 'bg-gray-700' : 'bg-gray-200'
                                  }`}>
                                    {mostRecentChat.is_group ? 
                                      <Users className="w-3 h-3" /> : 
                                      <span className="text-[8px]">{getInitials(mostRecentChat.title)}</span>
                                    }
                                  </div>
                                )}
                              </div>
                              <div className="truncate">
                                <span className={`text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                  {mostRecentChat.title}
                                </span>
                                <div className="flex items-center gap-1">
                                  <span className={`text-[9px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                    Son aktivite: {new Date(mostRecentChat.last_message_at).toLocaleTimeString([], {
                                      hour: '2-digit', 
                                      minute: '2-digit',
                                      hour12: false
                                    })}
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {activeChat ? (
          <>
            {/* Chat Header */}
            <div className={`p-3 border-b ${
              isDark ? 'border-gray-700' : 'border-gray-200'
            } flex items-center justify-between sticky top-0 z-10 ${
              isDark ? 'bg-gray-900/95' : 'bg-white/95'
            } backdrop-blur-sm shadow-sm`}>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                  className={`p-2 rounded-full ${
                    isDark 
                      ? 'hover:bg-gray-700 text-gray-300' 
                      : 'hover:bg-gray-100 text-gray-600'
                  } lg:hidden transition-colors`}
                >
                  {isSidebarOpen ? <PanelLeft className="w-5 h-5" /> : <PanelRight className="w-5 h-5" />}
                </button>
                
                {currentChat?.is_group ? (
                  // Grup avatarı
                  <div className="relative">
                    {currentChat.group_photo_url ? (
                      <img 
                        src={currentChat.group_photo_url} 
                        alt={currentChat.title} 
                        className="w-10 h-10 rounded-full object-cover border-2 border-gray-200 dark:border-gray-700 shadow-md"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = `
                            <div class="w-10 h-10 rounded-full flex items-center justify-center ${
                              isDark ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-700'
                            } font-semibold text-md shadow-md">
                              ${getInitials(currentChat?.title || '')}
                            </div>
                          `;
                        }}
                      />
                    ) : (
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        isDark ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-700'
                      } font-semibold text-md shadow-md`}>
                        {getInitials(currentChat?.title || '')}
                      </div>
                    )}
                    
                    {/* Grup için çevrimiçi göstergesi */}
                    {currentChat.members && currentChat.members.filter(m => m.online).length > 0 && (
                      <div className={`absolute -bottom-1 -right-1 px-1.5 py-0.5 rounded-full text-[7px] font-medium ${
                        isDark ? 'bg-green-900 text-green-300 border border-gray-800' : 'bg-green-100 text-green-800 border border-white'
                      }`}>
                        {currentChat.members.filter(m => m.online).length} çvr
                      </div>
                    )}
                  </div>
                ) : currentChat?.otherUser?.avatar ? (
                  // Diğer kullanıcının avatarı
                  <div className="relative">
                    <img 
                      src={currentChat.otherUser.avatar} 
                      alt={currentChat.otherUser.name} 
                      className="w-10 h-10 rounded-full object-cover border-2 border-gray-200 dark:border-gray-700 shadow-md"
                    />
                    {currentChat.otherUser.online && (
                      <div className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-white dark:border-gray-800 animate-pulse"></div>
                    )}
                  </div>
                ) : (
                  // Avatar yoksa baş harfler
                  <div className="relative">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isDark ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-700'
                    } font-semibold text-md shadow-md`}>
                      {getInitials(currentChat?.title || '')}
                    </div>
                    {currentChat?.otherUser?.online && (
                      <div className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-white dark:border-gray-800 animate-pulse"></div>
                    )}
                  </div>
                )}
                
                <div>
                  <h2 className={`text-base font-semibold ${
                    isDark ? 'text-white' : 'text-gray-900'
                  }`}>
                    {currentChat?.title}
                  </h2>
                  
                  {currentChat?.is_group ? (
                    <div className="flex items-center gap-1.5">
                      {/* Çevrimiçi üye sayısı */}
                      <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {currentChat.members?.length || 0} üye
                        {currentChat.members && 
                          ` • ${currentChat.members.filter(m => m.online).length} çevrimiçi`
                        }
                      </p>
                      
                      {/* Üye avatarları (en fazla 3 tanesi) */}
                      <div className="flex -space-x-1.5 ml-1">
                        {currentChat.members && currentChat.members.filter(m => m.online).slice(0, 3).map((member, idx) => (
                          <div key={member.id} className="relative">
                            {member.avatar ? (
                              <img 
                                src={member.avatar} 
                                alt={member.name} 
                                className="w-4 h-4 rounded-full object-cover border border-gray-800 dark:border-gray-700"
                                title={member.name}
                              />
                            ) : (
                              <div 
                                className={`w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-medium border border-gray-800 dark:border-gray-700 ${
                                  isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                                }`} 
                                title={member.name}
                              >
                                {getInitials(member.name).charAt(0)}
                              </div>
                            )}
                            <div className="absolute -bottom-0.5 -right-0.5 w-1.5 h-1.5 bg-green-500 rounded-full border border-white dark:border-gray-800"></div>
                          </div>
                        ))}
                        
                        {/* Daha fazla çevrimiçi üye varsa göster */}
                        {currentChat.members && currentChat.members.filter(m => m.online).length > 3 && (
                          <div 
                            className={`w-4 h-4 rounded-full flex items-center justify-center text-[7px] ${
                              isDark ? 'bg-gray-700 text-gray-300 border border-gray-800' : 'bg-gray-200 text-gray-700 border border-white'
                            }`}
                            title="Daha fazla çevrimiçi üye"
                          >
                            +{currentChat.members.filter(m => m.online).length - 3}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p className={`text-xs ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {currentChat?.otherUser?.online ? (
                        <span className="flex items-center gap-1">
                          <span className="flex h-1.5 w-1.5 relative">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-500"></span>
                          </span>
                          Çevrimiçi
                        </span>
                      ) : 'Son görülme: 2 saat önce'}
                    </p>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <button 
                     type="button"
                     onClick={() => {
                       toast.error('Sesli Arama hizmeti şu anda devre dışıdır. Lütfen daha sonra tekrar deneyiniz.');
                     }}
                  className={`p-2 rounded-full ${
                    isDark 
                      ? 'hover:bg-gray-700 text-gray-300' 
                      : 'hover:bg-gray-100 text-gray-600'
                  } transition-colors hidden sm:block`}
                >
                  <Phone className="w-5 h-5" />
                </button>
                <button 
                                     type="button"
                                     onClick={() => {
                                       toast.error('Görüntülü Arama hizmeti şu anda devre dışıdır. Lütfen daha sonra tekrar deneyiniz.');
                                     }}
                  className={`p-2 rounded-full ${
                    isDark 
                      ? 'hover:bg-gray-700 text-gray-300' 
                      : 'hover:bg-gray-100 text-gray-600'
                  } transition-colors hidden sm:block`}
                >
                  <Video className="w-5 h-5" />
                </button>
                <div className="relative" ref={headerDropdownRef}>
                  <button 
                    onClick={() => setShowHeaderDropdown(!showHeaderDropdown)}
                    className={`p-2 rounded-full ${
                      isDark 
                        ? 'hover:bg-gray-700 text-gray-300' 
                        : 'hover:bg-gray-100 text-gray-600'
                    } transition-colors ${showHeaderDropdown ? (isDark ? 'bg-gray-700' : 'bg-gray-200') : ''}`}
                  >
                    <MoreVertical className="w-5 h-5" />
                  </button>
                  
                  {/* Dropdown Menu */}
                  {showHeaderDropdown && (
                    <div 
                      className={`absolute right-0 mt-2 w-48 rounded-md shadow-lg z-20 ${
                        isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
                      }`}
                    >
                      <div className="py-1" role="menu" aria-orientation="vertical">
                        <button
                          onClick={() => {
                            setShowChatInfo(true);
                            setShowHeaderDropdown(false);
                          }}
                          className={`flex items-center w-full px-4 py-2 text-sm ${
                            isDark 
                              ? 'text-gray-300 hover:bg-gray-700' 
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          role="menuitem"
                        >
                          <Info className="w-4 h-4 mr-3" />
                          {currentChat?.is_group ? "Grup bilgisi" : "Kişi bilgisi"}
                        </button>
                        <button
                          onClick={() => {
                            setShowHeaderDropdown(false);
                          }}
                          className={`flex items-center w-full px-4 py-2 text-sm ${
                            isDark 
                              ? 'text-gray-300 hover:bg-gray-700' 
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          role="menuitem"
                        >
                          <Star className="w-4 h-4 mr-3" />
                          Favorilere Ekle
                        </button>
                      
                        
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Chat Messages */}
            <div 
              ref={messageContainerRef}
              className={`flex-1 overflow-y-auto p-4 ${
                isDark ? 'bg-gray-850' : 'bg-gray-50'
              } bg-opacity-70 backdrop-blur-sm relative`}
            >
              {/* Scroll to Bottom Button */}
              {showScrollBottom && (
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0, opacity: 0 }}
                  className="fixed bottom-20 right-10 z-10"
                >
                  <div className="relative">
                    {/* Yeni Mesaj Önizlemeleri */}
                    {unreadMessages.length > 0 && (
                      <div className="absolute bottom-full right-0 mb-2 w-64">
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className={`p-2 rounded-lg shadow-lg ${
                            isDark ? 'bg-gray-800' : 'bg-white border border-gray-200'
                          }`}
                        >
                          <div className="text-xs font-semibold mb-1 px-2">
                            <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>Yeni Mesajlar</span>
                          </div>
                          
                          {/* Her bir yeni mesaj için önizleme */}
                          {unreadMessages.map((msg, idx) => (
                            <motion.div 
                              key={msg.id || msg.message_id || idx}
                              initial={{ opacity: 0, x: 20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: idx * 0.1 }}
                              className={`p-2 rounded-md mb-1 text-sm ${
                                isDark 
                                  ? 'bg-gray-700 text-white' 
                                  : 'bg-gray-100 text-gray-800'
                              } flex items-start gap-2`}
                            >
                              {/* Avatar */}
                              {msg.sender_avatar ? (
                                <img 
                                  src={msg.sender_avatar}
                                  alt=""
                                  className="w-6 h-6 rounded-full flex-shrink-0"
                                />
                              ) : (
                                <div className="w-6 h-6 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center flex-shrink-0">
                                  {(msg.sender_name || '?').charAt(0).toUpperCase()}
                                </div>
                              )}
                              
                              <div className="overflow-hidden flex-1">
                                {/* Gönderen adı */}
                                <div className="text-xs font-semibold truncate">
                                  {msg.sender_name || 'Anonim'}
                                </div>
                                
                                {/* Mesaj metni */}
                                <div className="text-xs truncate">
                                  {msg.message_type === 'text' 
                                    ? msg.message_text 
                                    : msg.message_type === 'image' 
                                      ? '📷 Resim' 
                                      : msg.message_type === 'file' 
                                        ? '📎 Dosya' 
                                        : '✉️ Mesaj'}
                                </div>
                              </div>
                            </motion.div>
                          ))}
                          
                          <button 
                            onClick={scrollToBottom}
                            className={`text-xs w-full py-1 mt-1 rounded-md ${
                              isDark 
                                ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                                : 'bg-blue-100 hover:bg-blue-200 text-blue-800'
                            }`}
                          >
                            Tüm mesajları görüntüle
                          </button>
                        </motion.div>
                      </div>
                    )}
                    
                    {/* Dibe Git Butonu */}
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={scrollToBottom}
                      className={`p-3 rounded-full shadow-lg flex items-center justify-center ${
                        isDark 
                          ? 'bg-blue-600 text-white hover:bg-blue-700' 
                          : 'bg-white text-blue-600 border border-blue-100 hover:bg-blue-50'
                      } relative transition-colors`}
                    >
                      <ArrowDown className="w-5 h-5" />
                      
                      {/* Mesaj Sayacı */}
                      {unreadMessages.length > 0 && (
                        <div className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center animate-pulse">
                          {unreadMessages.length}
                        </div>
                      )}
                    </motion.button>
                  </div>
                </motion.div>
              )}

              {/* Load More Messages Button - Eski mesajları yüklemek için en ÜSTTE olması gerekiyor */}
              {hasMoreMessages && (
                <div className="flex justify-center my-2">
                  <button
                    onClick={loadMoreMessages}
                    disabled={messagesLoading}
                    className={`px-4 py-1.5 rounded-full text-xs shadow-sm transition-all duration-200 ${
                      isDark 
                        ? 'bg-gray-800 hover:bg-gray-700 text-gray-300 hover:shadow-md' 
                        : 'bg-white hover:bg-gray-100 text-gray-700 border border-gray-200 hover:shadow-md'
                    } transform hover:scale-105`}
                  >
                    {messagesLoading ? (
                      <span className="flex items-center gap-1">
                        <div className="w-3 h-3 border-2 border-t-transparent rounded-full animate-spin dark:border-gray-400 border-gray-400"></div>
                        <span className="text-gray-500 dark:text-gray-400">Yükleniyor...</span>
                      </span>
                    ) : (
                      <span className="flex items-center gap-1.5">
                        <ArrowDown className="w-3.5 h-3.5" />
                        <span>Daha eski mesajları yükle</span>
                      </span>
                    )}
                  </button>
                </div>
              )}

              {/* Date separator */}
              <div className="flex justify-center my-5">
                <div className={`px-4 py-1.5 rounded-full text-xs font-medium drop-shadow-sm ${
                  isDark ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-700 border border-gray-200'
                }`}>
                  {new Date().toLocaleDateString('tr-TR', { 
                    day: 'numeric', 
                    month: 'long', 
                    year: 'numeric', 
                    weekday: 'long'
                  })}
                </div>
              </div>

              {/* No messages placeholder */}
              {messagesLoading ? (
                <MessageSkeletonLoading />
              ) : !messagesLoading && messages.length === 0 ? (
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className={`flex flex-col items-center justify-center p-10 text-center ${
                    isDark ? 'text-gray-400' : 'text-gray-600'
                  } my-12`}
                >
                  <div className={`w-20 h-20 rounded-full mb-6 flex items-center justify-center ${
                    isDark ? 'bg-gray-800' : 'bg-gray-100'
                  }`}>
                    <MessageCircle className={`w-10 h-10 ${
                      isDark ? 'text-gray-500' : 'text-gray-400'
                    }`} />
                  </div>
                  <p className="mb-2 text-xl font-medium animate-pulse">Henüz bir mesaj yok</p>
                  <p className="mb-6 animate-pulse opacity-80">Sohbeti başlatmak için ilk mesajı gönder</p>
                  <div className={`w-24 h-12 ${isDark ? 'bg-gray-800' : 'bg-gray-100'} rounded-lg animate-pulse`}></div>
                </motion.div>
              ) : (
                // Messages
                messages.map((msg, index) => {
                  // Mesaj gruplamayı hesapla (aynı kişiden gelen ardışık mesajlar)
                  const isFirstInSequence = index === 0 || messages[index - 1].sender_id !== msg.sender_id;
                  const isLastInSequence = index === messages.length - 1 || messages[index + 1].sender_id !== msg.sender_id;
  
  return (
                    <motion.div 
                      key={msg.message_id || msg.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 > 1 ? 1 : index * 0.05 }}
                      className={`flex mb-1 ${
                        msg.message_type === 'system' 
                          ? 'justify-center' 
                          : msg.sender_id === currentUser?.id 
                            ? 'justify-end' 
                            : 'justify-start'
                      } ${!isLastInSequence ? 'mb-1' : 'mb-4'}`}
                    >
                      {/* Sistem mesajları için farklı görüntüleme */}
                      {msg.message_type === 'system' ? (
                        <div className={`max-w-[80%] py-2 px-4 rounded-full text-xs font-medium text-center ${
                          isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-600'
                        } my-3`}>
                          {renderMessageContent(msg)}
                        </div>
                      ) : (
                        // Normal mesajlar için standart görüntüleme
                        <>
                          {/* Avatar - sadece gönderen biz değilse göster ve sadece seri başlangıcında göster */}
                          {msg.sender_id !== currentUser?.id && isFirstInSequence && (
                            <div className="flex-shrink-0 mr-2 self-end mb-1">
                              {msg.sender_avatar ? (
                                <img 
                                  src={msg.sender_avatar} 
                                  alt={msg.sender_name || "Kullanıcı"} 
                                  className="w-9 h-9 rounded-full border-2 border-white dark:border-gray-800 shadow-sm"
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = `/avatar-placeholder.png`;
                                  }}
                                />
                              ) : (
                                <div className="w-9 h-9 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-sm font-semibold text-white shadow-sm border-2 border-white dark:border-gray-800">
                                  {(msg.sender_name || "?").charAt(0).toUpperCase()}
                                </div>
                              )}
                            </div>
                          )}
                          
                          {/* Avatar için yer tutucu (diğer mesajlar için) */}
                          {msg.sender_id !== currentUser?.id && !isFirstInSequence && (
                            <div className="w-11 flex-shrink-0"></div>
                          )}
                          
                          {/* Message content */}
                          <div 
                            className={`max-w-[70%] ${
                              msg.message_type === 'image' ? 'max-w-md' : ''
                            }`}
                          >
                            {/* Sender name, only show for others and only for first consecutive message */}
                            {msg.sender_id !== currentUser?.id && isFirstInSequence && (
                              <div className="text-xs font-medium ml-1 mb-1">
                                <span className={`${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                  {msg.sender_name || "İsimsiz Kullanıcı"}
                                </span>
                                {msg.sender_role && (
                                  <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] ${
                                    isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-600'
                                  }`}>
                                    {msg.sender_role}
                                  </span>
                                )}
                              </div>
                            )}
                            
                            {/* Message bubble */}
                            <div 
                              className={`p-3 shadow-sm ${
                                msg.sender_id === currentUser?.id 
                                  ? `dark: bg-white/10 
                                    backdrop-blur-md 
                                    border border-white/20 dark:border-white/10 
                                    shadow-lg 
                                    text-gray-800 dark:text-gray-100 
                                    transition duration-300 
                                    hover:backdrop-blur-lg hover:bg-white/40 dark:hover:bg-white/20
                                    hover:shadow-lg hover:shadow-gray-300/20 dark:hover:shadow-gray-700/20
                                    ${
                                      isFirstInSequence && isLastInSequence 
                                        ? 'rounded-xl rounded-tr-sm' 
                                        : isFirstInSequence 
                                          ? 'rounded-xl rounded-tr-sm rounded-br-md' 
                                          : isLastInSequence 
                                            ? 'rounded-xl rounded-tr-md rounded-tl-md' 
                                            : 'rounded-xl rounded-tr-md rounded-br-md'
                                    }`
                                  : isDark 
                                    ? `bg-gray-700 text-white ${
                                        isFirstInSequence && isLastInSequence 
                                          ? 'rounded-xl rounded-tl-sm' 
                                          : isFirstInSequence 
                                            ? 'rounded-xl rounded-tl-sm rounded-bl-md' 
                                            : isLastInSequence 
                                              ? 'rounded-xl rounded-tl-md rounded-tr-md' 
                                              : 'rounded-xl rounded-tl-md rounded-bl-md'
                                      }`
                                    : `bg-white border border-gray-200 text-gray-800 ${
                                        isFirstInSequence && isLastInSequence 
                                          ? 'rounded-xl rounded-tl-sm' 
                                          : isFirstInSequence 
                                            ? 'rounded-xl rounded-tl-sm rounded-bl-md' 
                                            : isLastInSequence 
                                              ? 'rounded-xl rounded-tl-md rounded-tr-md' 
                                              : 'rounded-xl rounded-tl-md rounded-bl-md'
                                      }`
                              }`}
                            >
                              <div className={msg.message_type === 'text' ? 'break-words' : ''}>
                                {renderMessageContent(msg)}
                              </div>
                            </div>
                            
                            {/* Time and status - only show for the last message in sequence */}
                            {isLastInSequence && (
                              <div className={`flex items-center mt-1 text-[10px] ${
                                msg.sender_id === currentUser?.id ? 'justify-end mr-1' : 'justify-start ml-1'
                              }`}>
                                <span className={`${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                                  {new Date(msg.created_at).toLocaleTimeString('tr-TR', {
                                    hour: '2-digit', 
                                    minute: '2-digit',
                                    hour12: false
                                  })}
                                </span>
                                
                                {/* Read status for our messages */}
                                {msg.sender_id === currentUser?.id && (
                                  <span className="ml-1 flex">
                                    {msg.is_read ? (
                                      <Check className="w-3 h-3 text-blue-500" />
                                    ) : (
                                      <Check className="w-3 h-3 text-gray-400" />
                                    )}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                          
                          {/* Spacer for our messages, to align with avatar */}
                          {msg.sender_id === currentUser?.id && (
                            <div className="w-9 flex-shrink-0 ml-2"></div>
                          )}
                        </>
                      )}
                    </motion.div>
                  );
                })
              )}
              
              <div ref={messageEndRef} />
            </div>
            
            {/* File Preview Area - Show before upload */}
            {filePreview && (
              <div className={`px-4 py-3 border-t ${
                isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {filePreview.type === 'image' ? (
                      <div className="relative h-14 w-14 rounded-md overflow-hidden">
                        <img src={filePreview.url} alt="Önizleme" className="h-full w-full object-cover" />
                      </div>
                    ) : (
                      <div className={`h-14 w-14 rounded-md flex items-center justify-center ${
                        filePreview.fileType?.includes('pdf') ? 'bg-red-100' :
                        filePreview.fileType?.includes('word') ? 'bg-blue-100' :
                        filePreview.fileType?.includes('text') ? 'bg-gray-100' :
                        filePreview.fileType?.includes('excel') || filePreview.fileType?.includes('spreadsheet') ? 'bg-green-100' :
                        filePreview.fileType?.includes('powerpoint') || filePreview.fileType?.includes('presentation') ? 'bg-orange-100' : 'bg-blue-100'
                      }`}>
                        <FileText className={`w-8 h-8 ${
                          filePreview.fileType?.includes('pdf') ? 'text-red-500' :
                          filePreview.fileType?.includes('word') ? 'text-blue-600' :
                          filePreview.fileType?.includes('text') ? 'text-gray-600' :
                          filePreview.fileType?.includes('excel') || filePreview.fileType?.includes('spreadsheet') ? 'text-green-600' :
                          filePreview.fileType?.includes('powerpoint') || filePreview.fileType?.includes('presentation') ? 'text-orange-600' : 'text-blue-500'
                        }`} />
                      </div>
                    )}
                    <div>
                      <div className="text-sm font-medium truncate max-w-[200px]">
                        {filePreview.name}
                      </div>
                      <div className="text-xs opacity-70">
                        {filePreview.size}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={cancelFileUpload}
                      className={`p-2 rounded-full ${
                        isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-200 text-gray-600'
                      }`}
                      title="İptal"
                    >
                      <X className="w-5 h-5" />
                    </button>
                    <button
                      type="button"
                      onClick={confirmFileUpload}
                      className="p-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white"
                      title="Gönder"
                    >
                      <Send className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Message Input */}
            <div className={`p-3 border-t rounded-br-[24px] rounded-tr-[24px] ${
              isDark ? 'border-gray-700' : 'border-gray-200'
            } sticky bottom-0 z-10 ${
              isDark ? 'bg-gray-900' : 'bg-white'
            }`}>
              <form 
                onSubmit={handleSendMessage}
                className="flex items-center gap-2"
              >
                {/* Dosya yükleme input'u (gizli) */}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleAttachmentUpload}
                  className="hidden"
                  accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                />
                
                {/* Ataç butonu - Dosya ekleme */}
                <button 
                  type="button"
                  onClick={() => {
                    // Dosya yükleme özelliği devre dışı olduğunu belirt
                    toast.error('Dosya yükleme hizmetimiz şu anda devre dışıdır. Lütfen daha sonra tekrar deneyiniz.');
                  }}
                  className={`p-2 rounded-full ${
                    isDark 
                      ? 'hover:bg-gray-700 text-gray-300 hover:text-blue-400' 
                      : 'hover:bg-gray-100 text-gray-600 hover:text-blue-600'
                  } transition-colors relative group`}
                  title="Dosya yükleme özelliği şu anda devre dışı"
                >
                  <Paperclip className="w-5 h-5" />
                  <span 
                    className={`absolute -top-10 left-1/2 transform -translate-x-1/2 px-2 py-1 text-xs font-medium rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 ${
                      isDark ? 'bg-gray-700 text-white' : 'bg-white text-gray-800 shadow-md'
                    }`}
                  >
                    Hizmet Dışı
                  </span>
                </button>
                
                <div className={`flex-1 flex items-center rounded-[20px] p-1 pl-4 ${
                  isDark ? 'bg-gray-800' : 'bg-gray-100'
                }`}>
                  <textarea
                    value={message}
                    onChange={handleMessageChange}
                    placeholder="Birşeyler yazın..."
                    className={`bg-transparent border-none outline-none focus:outline-none focus:ring-0 focus:border-transparent w-full resize-none py-2 max-h-32 overflow-y-auto ${
                      isDark ? 'text-white placeholder-gray-400' : 'text-gray-900 placeholder-gray-500'
                    }`}
                    style={{ minHeight: '24px' }}
                    rows="1"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage(e);
                      }
                    }}
                  />
                  <div className="flex">
                  <button 
     type="button"
     onClick={() => {
       toast.error('Görsel yükleme hizmeti şu anda devre dışıdır. Lütfen daha sonra tekrar deneyiniz.');
     }}
     className={`p-2 rounded-full ${
       isDark 
         ? 'hover:bg-gray-700 text-gray-300' 
         : 'hover:bg-gray-200 text-gray-600'
     } transition-colors relative group`}
     title="Görsel Yükleme"
   >
     <Image className="w-5 h-5" />
     <span 
       className={`absolute -top-10 left-1/2 transform -translate-x-1/2 px-2 py-1 text-xs font-medium rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 ${
         isDark ? 'bg-gray-700 text-white' : 'bg-white text-gray-800 shadow-md'
       }`}
     >
       Hizmet Dışı
     </span>
   </button>
                    <button 
     type="button"
     onClick={() => {
       toast.error('Sesli mesaj hizmeti şu anda devre dışıdır. Lütfen daha sonra tekrar deneyiniz.');
     }}
     className={`p-2 rounded-full ${
       isDark 
         ? 'hover:bg-gray-700 text-gray-300' 
         : 'hover:bg-gray-200 text-gray-600'
     } transition-colors relative group`}
     title="Sesli Mesaj"
   >
     <Mic className="w-5 h-5" />
     <span 
       className={`absolute -top-10 left-1/2 transform -translate-x-1/2 px-2 py-1 text-xs font-medium rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 ${
         isDark ? 'bg-gray-700 text-white' : 'bg-white text-gray-800 shadow-md'
       }`}
     >
       Hizmet Dışı
     </span>
   </button>
                    <button 
     type="button"
     onClick={() => {
       toast.error('Emoji seçme hizmeti şu anda devre dışıdır. Lütfen daha sonra tekrar deneyiniz.');
     }}
     className={`p-2 rounded-full ${
       isDark 
         ? 'hover:bg-gray-700 text-gray-300' 
         : 'hover:bg-gray-200 text-gray-600'
     } transition-colors relative group`}
     title="Emoji Seç"
   >
     <Smile className="w-5 h-5" />
     <span 
       className={`absolute -top-10 left-1/2 transform -translate-x-1/2 px-2 py-1 text-xs font-medium rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 ${
         isDark ? 'bg-gray-700 text-white' : 'bg-white text-gray-800 shadow-md'
       }`}
     >
       Hizmet Dışı
     </span>
   </button>
                  </div>
                </div>
                
                <button 
                  type="submit"
                  disabled={!message.trim()}
                  className={`p-3 rounded-full ${
                    message.trim()
                      ? (isDark ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-600 hover:bg-blue-700')
                      : (isDark ? 'bg-gray-700 cursor-not-allowed' : 'bg-gray-300 cursor-not-allowed')
                  } text-white transition-colors`}
                >
                  <Send className="w-5 h-5" />
                </button>
              </form>
            </div>
          </>
        ) : (
          // No chat selected view
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center p-6">
              <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${
                isDark ? 'bg-gray-800' : 'bg-gray-100'
              } mb-4`}>
                <MessageCircle className={`w-8 h-8 ${
                  isDark ? 'text-gray-400' : 'text-gray-500'
                }`} />
              </div>
              <h3 className={`text-xl font-bold mb-2 ${
                isDark ? 'text-white' : 'text-gray-900'
              }`}>
                Sohbet Başlatılmadı
              </h3>
              <p className={`max-w-sm mb-6 animate-pulse ${
                isDark ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Bir sohbet başlatmak için soldaki listeden bir kişi seçin veya yeni bir sohbet başlatın.
              </p>
              <button onClick={() => setShowNewChatModal(true)}
              className={`px-3 py-1.5 rounded-md border ${isDark 
              ? 'bg-transparent border-gray-600 text-gray-200 hover:bg-gray-700' 
              : 'bg-transparent border-gray-300 text-gray-800 hover:bg-gray-100'
              } text-sm inline-flex items-center gap-1 transition-colors duration-200`}>
              <Plus className="w-4 h-4" />
              <span>Yeni Sohbet</span>
              </button>

            </div>
          </div>
        )}
      </div>

      {/* Yeni Sohbet Başlatma Modalı */}
      <AnimatePresence>
        {showNewChatModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className={`w-full max-w-lg rounded-xl shadow-2xl ${
                isDark ? 'bg-gray-800' : 'bg-white'
              } overflow-hidden`}
            >
              {/* Modal Header */}
              <div className={`p-4 border-b ${
                isDark ? 'border-gray-700' : 'border-gray-200'
              } flex items-center justify-between`}>
                <h2 className={`text-lg font-semibold ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}>
                  {chatModalTab === 'direct' ? 'Yeni Sohbet Başlat' : 'Yeni Grup Oluştur'}
                </h2>
                <button
                  onClick={() => setShowNewChatModal(false)}
                  className={`p-2 rounded-full ${
                    isDark 
                      ? 'hover:bg-gray-700 text-gray-300' 
                      : 'hover:bg-gray-100 text-gray-600'
                  }`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Modal Tabs */}
              <div className={`px-4 py-2 border-b ${
                isDark ? 'border-gray-700' : 'border-gray-200'
              } flex items-center gap-2`}>
                <button
                  onClick={() => setChatModalTab('direct')}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                    chatModalTab === 'direct' 
                      ? (isDark ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-800') 
                      : (isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-600')
                  }`}
                >
                  Kişi Sohbeti
                </button>
                <button
                  onClick={() => setChatModalTab('group')}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                    chatModalTab === 'group' 
                      ? (isDark ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-800') 
                      : (isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-600')
                  }`}
                >
                  Grup Oluştur
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-4">
                {chatModalTab === 'direct' ? (
                  <>
                    {/* Direct Chat Content */}
                    <div className={`flex items-center gap-2 p-2 rounded-lg mb-4 rounded-br-[24px] rounded-tr-[10px] ${
                      isDark 
                        ? 'bg-gray-700 text-gray-300' 
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      <Search className="w-5 h-5" />
                      <input
                        type="text"
                        placeholder="Kişi ara..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className={`bg-transparent border-none outline-none w-full rounded-br-[18px] rounded-bl-[6px] rounded-tl-[6px] rounded-tr-[4px] ${
                          isDark ? 'placeholder-gray-500 text-white' : 'placeholder-gray-400 text-gray-900'
                        }`}
                      />
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery('')}
                          className="p-1 rounded-full hover:bg-gray-600/50"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    {/* Users List */}
                    <div className="max-h-60 overflow-y-auto">
                      {loading ? (
                        <div className="p-8 flex justify-center items-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                        </div>
                      ) : filteredUsers.length > 0 ? (
                        filteredUsers.map(user => (
                          <div 
                            key={user.id}
                            className={`flex items-center justify-between p-3 rounded-lg mb-2 cursor-pointer ${
                              isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                            } transition-colors`}
                            onClick={() => handleStartNewChat(user)}
                          >
                            <div className="flex items-center gap-3">
                              {user.avatar ? (
                                <img 
                                  src={user.avatar} 
                                  alt={user.name} 
                                  className="w-10 h-10 rounded-full object-cover"
                                />
                              ) : (
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-md font-semibold ${
                                  isDark ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-700'
                                }`}>
                                  {getInitials(user.name)}
                                </div>
                              )}
                              <div>
                                <h3 className={`text-sm font-medium ${
                                  isDark ? 'text-white' : 'text-gray-900'
                                }`}>
                                  {user.name}
                                </h3>
                                <p className={`text-xs ${
                                  isDark ? 'text-gray-400' : 'text-gray-500'
                                }`}>
                                  {user.job_position || user.email || ''}
                                </p>
                              </div>
                            </div>
                            <button className={`p-2 rounded-full ${
                              isDark ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-600 hover:bg-blue-700'
                            } text-white`}>
                              <UserPlus className="w-4 h-4" />
                            </button>
                          </div>
                        ))
                      ) : (
                        <div className={`p-6 text-center ${
                          isDark ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                          <p>Kişi bulunamadı</p>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    {/* Group Chat Content */}
                    <div className="mb-4">
                      <label className={`block mb-2 text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        Grup Adı
                      </label>
                      <input
                        type="text"
                        value={groupName}
                        onChange={(e) => setGroupName(e.target.value)}
                        placeholder="Grubun adını girin..."
                        className={`w-full p-3 rounded-lg  ${
                          isDark 
                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                            : 'bg-gray-100 border-gray-200 text-gray-900 placeholder-gray-500'
                        } border outline-none focus:ring-2 focus:ring-blue-500`}
                      />
                    </div>
                    
                    {/* Grup Fotoğrafı Ekleme */}
                    <div className="mb-4">
                      <label className={`block mb-2 text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        Grup Fotoğrafı
                      </label>
                      <div className="flex items-center gap-4">
                        {groupPhotoPreview ? (
                          <div className="relative">
                            <img 
                              src={groupPhotoPreview} 
                              alt="Grup fotoğrafı önizleme" 
                              className="w-16 h-16 object-cover rounded-full"
                            />
                            <button
                              onClick={removeGroupPhoto}
                              className={`absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-md`}
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <div className={`w-16 h-16 flex items-center justify-center rounded-full ${
                            isDark ? 'bg-gray-700' : 'bg-gray-200'
                          }`}>
                            {groupName ? (
                              <span className={`text-xl font-semibold ${
                                isDark ? 'text-gray-300' : 'text-gray-700'
                              }`}>
                                {getInitials(groupName)}
                      </span>
                            ) : (
                              <Users className={`w-8 h-8 ${
                                isDark ? 'text-gray-400' : 'text-gray-500'
                              }`} />
                            )}
                          </div>
                        )}
                        <div>
                          <label
                            htmlFor="group-photo-upload"
                            className={`cursor-pointer inline-flex items-center gap-2 px-3 py-2 rounded-md ${
                              isDark
                                ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                                : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                            }`}
                          >
                            <Upload className="w-4 h-4" />
                            <span>{groupPhotoPreview ? 'Değiştir' : 'Yükle'}</span>
                          </label>
                          <input 
                            type="file"
                            id="group-photo-upload"
                            accept="image/*"
                            onChange={handleGroupPhotoSelect}
                            className="hidden"
                          />
                          <p className={`text-xs mt-1 ${
                            isDark ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                            Max 5MB (jpg, png)
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Selected Members List */}
                    {selectedGroupMembers.length > 0 && (
                      <div className="mb-4">
                        <label className={`block mb-2 text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          Seçilen Üyeler ({selectedGroupMembers.length})
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {selectedGroupMembers.map(member => (
                            <div 
                              key={member.id}
                              className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${
                                isDark ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-800'
                              }`}
                            >
                              <span className="text-sm">{member.name.split(' ')[0]}</span>
                              <button 
                                onClick={() => removeGroupMember(member.id)}
                                className={`p-0.5 rounded-full ${
                                  isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-300'
                                }`}
                              >
                                <X className="w-3 h-3" />
                    </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Search Members */}
                    <div className={`flex items-center gap-2 p-2 rounded-lg mb-4 rounded-br-[24px] rounded-tr-[10px] ${
                      isDark 
                        ? 'bg-gray-700 text-gray-300' 
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      <Search className="w-5 h-5" />
                      <input
                        type="text"
                        placeholder="Üye ara..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className={`bg-transparent border-none outline-none w-full rounded-br-[18px] rounded-bl-[6px] rounded-tl-[6px] rounded-tr-[4px] ${
                          isDark ? 'placeholder-gray-500 text-white' : 'placeholder-gray-400 text-gray-900'
                        }`}
                      />
                      {searchQuery && (
                    <button 
                          onClick={() => setSearchQuery('')}
                          className="p-1 rounded-full hover:bg-gray-600/50"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    {/* User Selection List for Group */}
                    <div className="max-h-60 overflow-y-auto">
                      {loading ? (
                        <div className="p-8 flex justify-center items-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                        </div>
                      ) : filteredUsers.length > 0 ? (
                        filteredUsers.map(user => (
                          <div 
                            key={user.id}
                            className={`flex items-center justify-between p-3 rounded-lg mb-2 cursor-pointer ${
                              isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                            } transition-colors`}
                            onClick={() => toggleGroupMember(user)}
                          >
                            <div className="flex items-center gap-3">
                              {user.avatar ? (
                                <img 
                                  src={user.avatar} 
                                  alt={user.name} 
                                  className="w-10 h-10 rounded-full object-cover"
                                />
                              ) : (
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-md font-semibold ${
                                  isDark ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-700'
                                }`}>
                                  {getInitials(user.name)}
                                </div>
                              )}
                              <div>
                                <h3 className={`text-sm font-medium ${
                                  isDark ? 'text-white' : 'text-gray-900'
                                }`}>
                                  {user.name}
                                </h3>
                                <p className={`text-xs ${
                                  isDark ? 'text-gray-400' : 'text-gray-500'
                                }`}>
                                  {user.job_position || user.email || ''}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center">
                              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                                selectedGroupMembers.some(member => member.id === user.id)
                                  ? (isDark ? 'bg-blue-600 border-blue-500' : 'bg-blue-600 border-blue-500')
                                  : (isDark ? 'border-gray-600' : 'border-gray-300')
                              }`}>
                                {selectedGroupMembers.some(member => member.id === user.id) && (
                                  <Check className="w-4 h-4 text-white" />
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className={`p-6 text-center ${
                          isDark ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                          <p>Kişi bulunamadı</p>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* Modal Footer */}
              <div className={`p-4 border-t ${
                isDark ? 'border-gray-700' : 'border-gray-200'
              } flex justify-end gap-3`}>
                <button
                  onClick={() => {
                    setShowNewChatModal(false);
                    setChatModalTab('direct');
                    setGroupName('');
                    setSelectedGroupMembers([]);
                  }}
                  className={`px-4 py-2 rounded-lg ${
                    isDark 
                      ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                  }`}
                >
                  İptal
                </button>

                {chatModalTab === 'group' && (
                  <button
                    onClick={handleCreateGroup}
                    disabled={groupCreating || !groupName.trim() || selectedGroupMembers.length < 2}
                    className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                      groupCreating || !groupName.trim() || selectedGroupMembers.length < 2
                        ? (isDark ? 'bg-gray-600 cursor-not-allowed text-gray-400' : 'bg-gray-300 cursor-not-allowed text-gray-500') 
                        : (isDark ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white')
                    }`}
                  >
                    {groupCreating ? (
                      <>
                        <div className="w-4 h-4 border-2 border-gray-300 border-t-white rounded-full animate-spin"></div>
                        <span>Oluşturuluyor...</span>
                      </>
                    ) : (
                      <>
                        <Users className="w-4 h-4" />
                        <span>Grup Oluştur</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      
      
      {/* Chat Info Modal */}
      <AnimatePresence>
        {showChatInfo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className={`w-full max-w-lg rounded-xl shadow-xl ${
                isDark ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-800'
              } overflow-hidden`}
            >
              {/* Header */}
              <div className={`px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'} flex justify-between items-center`}>
                <h3 className="text-lg font-semibold">
                  {currentChat?.is_group ? 'Grup Bilgisi' : 'Kişi Bilgisi'}
                </h3>
                <button 
                  onClick={() => setShowChatInfo(false)}
                      className={`p-2 rounded-full ${
                    isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-600'
                  }`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Content */}
              <div className="p-6">
                {currentChat?.is_group ? (
                  // Grup bilgisi
                  <div className="space-y-6">
                    {/* Grup avatarı ve adı */}
                    <div className="flex flex-col items-center justify-center space-y-4">
                      <div className={`w-24 h-24 rounded-full flex items-center justify-center ${
                        isDark ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-700'
                      }`}>
                        <img src={currentChat?.group_photo_url} alt={currentChat?.title} className="w-24 h-24 rounded-full object-cover" />

                      </div>
                      
                      <div className="text-center">
                        <h4 className="text-xl font-bold">{currentChat?.title}</h4>
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {currentChat?.created_at ? `Oluşturulma: ${new Date(currentChat.created_at).toLocaleDateString()}` : 'Grup bilgisi yükleniyor...'}
                        </p>
                      </div>
                    </div>
                    
                    {/* Grup istatistikleri */}
                  <div className={`grid grid-cols-3 gap-2 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-100'} p-4`}>
                    <div className="flex flex-col items-center justify-center">
                      <div className={`text-xl font-bold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                        {currentChat?.members?.length || 0}
                      </div>
                      <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Üye
                      </div>
                    </div>
                    
                    <div className="flex flex-col items-center justify-center">
                      <div className={`text-xl font-bold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                        {currentChat?.members?.filter(member => member.online)?.length || 0}
                      </div>
                      <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Çevrimiçi
                      </div>
                    </div>
                    
                    <div className="flex flex-col items-center justify-center">
                      <div className={`text-xl font-bold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                        {currentChat?.messageCount || 0}
                      </div>
                      <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Mesaj
                      </div>
                    </div>
                  </div>
                  
                    {/* Grup açıklaması */}
                    <div>
                      <h5 className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Grup Açıklaması</h5>
                      <p className={`p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                        {(() => {
  // Kontrol ediyoruz
  // console.log("CurrentChat:", currentChat);
  // console.log("Metadata:", currentChat?.metadata);
  
  if (currentChat?.metadata) {
    // Metadata string ise parse et
    if (typeof currentChat.metadata === 'string') {
      try {
        const parsed = JSON.parse(currentChat.metadata);
        //console.log("Parsed metadata:", parsed);
          if (parsed && parsed.description) {
            return parsed.description;
        }
      } catch (e) {
        console.error("Metadata parse error:", e);
          }
        }
        // Metadata obje ise doğrudan description'a eriş
    else if (typeof currentChat.metadata === 'object') {
      // console.log("Metadata as object:", currentChat.metadata);
      if (currentChat.metadata.description) {
        return currentChat.metadata.description;
      }
    }
  }
  
  // Fallback
  return currentChat?.description || 'Bu grup için henüz bir açıklama eklenmemiş.';
})()}
                      </p>
                    </div>
                    
                    {/* Grup üyeleri */}
                    <div>
                      <h5 className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                        Grup Üyeleri ({currentChat?.members?.length || 0})
                      </h5>
                      <div className={`rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-100'} p-1 max-h-60 overflow-y-auto`}>
                        {currentChat?.members?.length > 0 ? (
                          <ul className="divide-y divide-gray-700">
                            {currentChat.members.map((member, index) => (
                              <li key={index} className="flex items-center p-3">
                                {member.avatar ? (
                                  <img src={member.avatar} alt={member.name} className="w-10 h-10 rounded-full mr-3" />
                                ) : (
                                  <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 ${
                                    isDark ? 'bg-gray-600 text-gray-300' : 'bg-gray-300 text-gray-700'
                                  }`}>
                                    {getInitials(member.name)}
                                  </div>
                                )}
                                <div>
                                  <div className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                                    {member.name}
                        </div>
                        <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {member.role === 'admin' ? 'Grup Yöneticisi' : 'Üye'}
                                  </div>
                                </div>
                                {member.online && (
                                  <div className="ml-auto">
                                    <span className="relative flex h-3 w-3">
                                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                      <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                                    </span>
                                  </div>
                                )}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <div className="p-3 text-center text-gray-500">Üye bilgisi yükleniyor...</div>
                        )}
                        </div>
                      </div>
                      
                    {/* Grup Ayarları */}
                    <div>
                      <h5 className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Grup Ayarları</h5>
                      <div className="space-y-2">
<button
  onClick={() => {
    setShowAddMemberModal(true);
    fetchPotentialGroupMembers(currentChat?.id);
  }}
  className={`w-full flex items-center justify-between p-3 rounded-lg ${
    isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
  }`}
>
  <span className="flex items-center">
    <UserPlus className="w-5 h-5 mr-2" />
    <span>Üye Ekle</span>
  </span>
  <ChevronRight className="w-5 h-5" />
</button>
                        
                        <button
                          onClick={() => {
                            setNewGroupName(currentChat?.title || '');
                            setIsEditingGroupName(true);
                          }}
                          className={`w-full flex items-center justify-between p-3 rounded-lg ${
                            isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
                          }`}
                        >
                          <span className="flex items-center">
                            <PenSquare className="w-5 h-5 mr-2" />
                            <span>Grup Adını Düzenle</span>
                          </span>
                          <ChevronRight className="w-5 h-5" />
                        </button>
                        
                        <button
  onClick={() => {
    // Mevcut açıklamayı al
    let currentDesc = '';
    if (currentChat?.metadata) {
      if (typeof currentChat.metadata === 'string') {
        try {
          const parsed = JSON.parse(currentChat.metadata);
          currentDesc = parsed.description || '';
        } catch (e) {}
      } else if (typeof currentChat.metadata === 'object') {
        currentDesc = currentChat.metadata.description || '';
      }
    } else {
      currentDesc = currentChat?.description || '';
    }
    setNewGroupDesc(currentDesc);
    setIsEditingGroupDesc(true);
  }}
  className={`w-full flex items-center justify-between p-3 rounded-lg ${
    isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
  }`}
>
                          <span className="flex items-center">
                            <UserPlus className="w-5 h-5 mr-2" />
                            <span>Grup Açıklamasını Düzenle</span>
                          </span>
                          <ChevronRight className="w-5 h-5" />
                        </button>
                        
                        <button
                          onClick={() => {
                            setIsChangingGroupPhoto(true);
                          }}
                          className={`w-full flex items-center justify-between p-3 rounded-lg ${
                            isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
                          }`}
                        >
                          <span className="flex items-center">
                            <Camera className="w-5 h-5 mr-2" />
                            <span>Grup Fotoğrafını Değiştir</span>
                          </span>
                          <ChevronRight className="w-5 h-5" />
                        </button>
                        
                        {/*< button
                          onClick={() => {
                            setShowManageMembersModal(true);
                          }}
                          className={`w-full flex items-center justify-between p-3 rounded-lg ${
                            isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
                          }`}
                        >
                          <span className="flex items-center">
                            <UserCog className="w-5 h-5 mr-2" />
                            <span>Üyeleri Yönet</span>
                          </span>
                          <ChevronRight className="w-5 h-5" />
                        </button>
                        */}
                        
                        <button
                          onClick={() => {
                            // İleri düzey bir uygulama burada bir onay iletişim kutusu gösterebilir
                            if (confirm('Gruptan ayrılmak istediğinize emin misiniz?')) {
                              leaveGroup(activeChat);
                              setShowChatInfo(false);
                            }
                          }}
                          className={`w-full flex items-center justify-between p-3 rounded-lg ${
                            isDark ? 'bg-red-900/30 hover:bg-red-900/50 text-red-400' : 'bg-red-100 hover:bg-red-200 text-red-600'
                          }`}
                        >
                          <span className="flex items-center">
                            <LogOut className="w-5 h-5 mr-2" />
                            <span>Gruptan Ayrıl</span>
                          </span>
                          <ChevronRight className="w-5 h-5" />
                        </button>
                        </div>
                    </div>
                  </div>
                ) : (
                  // Kişi bilgisi
                  <div className="space-y-6">
                    {/* Kişi avatarı ve adı */}
                    <div className="flex flex-col items-center justify-center space-y-4">
                      {currentChat?.otherUser?.avatar ? (
                        <img 
                          src={currentChat.otherUser.avatar} 
                          alt={currentChat.otherUser.name}
                          className="w-24 h-24 rounded-full object-cover"
                          />
                        ) : (
                          <div className={`w-24 h-24 rounded-full flex items-center justify-center ${
                            isDark ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-700'
                        } text-2xl font-semibold`}>
                          {getInitials(currentChat?.title || '')}
                          </div>
                        )}
                      
                      <div className="text-center">
                        <h4 className="text-xl font-bold">{currentChat?.title}</h4>
                        <div className="flex items-center justify-center mt-1">
                          {currentChat?.otherUser?.online ? (
                            <div className="flex items-center text-green-500">
                              <span className="relative flex h-2.5 w-2.5 mr-1.5">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
                              </span>
                              <span className="text-sm">Çevrimiçi</span>
                            </div>
                          ) : (
                            <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              Son görülme: 2 saat önce
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Kişi bilgileri */}
                    <div>
                      <h5 className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                        İletişim Bilgileri
                      </h5>
                      <div className={`rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-100'} divide-y ${
                        isDark ? 'divide-gray-600' : 'divide-gray-200'
                      } overflow-hidden`}>
                        {currentChat?.otherUser?.email && (
                          <div className="p-3 flex items-center">
                            <Mail className={`w-5 h-5 mr-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                            <div>
                        <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                E-posta
                        </div>
                              <div className={isDark ? 'text-gray-200' : 'text-gray-800'}>
                                {currentChat.otherUser.email}
                      </div>
                            </div>
                          </div>
                        )}
                        
                        {currentChat?.otherUser?.job_position && (
                          <div className="p-3 flex items-center">
                            <Briefcase className={`w-5 h-5 mr-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                            <div>
                              <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                Pozisyon
                        </div>
                              <div className={isDark ? 'text-gray-200' : 'text-gray-800'}>
                                {currentChat.otherUser.job_position}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Ortak Dosyalar */}
                    <div>
                      <h5 className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                        Paylaşılan Medya ve Dosyalar
                      </h5>
                      <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-100'} text-center`}>
                        <div className={`mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          <FileText className="w-8 h-8 mx-auto mb-2 opacity-60" />
                          <p>Henüz paylaşılan dosya bulunmuyor.</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Kişi Ayarları */}
                    <div>
                      <h5 className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                        Kişi Ayarları
                      </h5>
                      <div className="space-y-2">
                        <button
                          onClick={() => toggleMuteChat(activeChat)}
                          className={`w-full flex items-center justify-between p-3 rounded-lg ${
                            isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
                          }`}
                        >
                          <span className="flex items-center">
                            <BellOff className="w-5 h-5 mr-2" />
                            <span>{mutedChats.includes(activeChat) ? 'Bildirimleri Aç' : 'Kişiyi Sessize Al'}</span>
                          </span>
                          <ChevronRight className="w-5 h-5" />
                        </button>
                        
                        <button
                          onClick={() => {
                            setShowSearchChatModal(true);
                            setChatSearchQuery('');
                            setChatSearchResults([]);
                          }}
                          className={`w-full flex items-center justify-between p-3 rounded-lg ${
                            isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
                          }`}
                        >
                          <span className="flex items-center">
                            <Search className="w-5 h-5 mr-2" />
                            <span>Sohbette Ara</span>
                          </span>
                          <ChevronRight className="w-5 h-5" />
                        </button>
                        
                        <button
                          onClick={() => {
                            if (confirm('Bu kişiyi engellemek istediğinize emin misiniz?')) {
                              toggleBlockUser(currentChat?.otherUser?.id, currentChat?.title);
                              setShowChatInfo(false);
                            }
                          }}
                          className={`w-full flex items-center justify-between p-3 rounded-lg ${
                            isDark ? 'bg-red-900/30 hover:bg-red-900/50 text-red-400' : 'bg-red-100 hover:bg-red-200 text-red-600'
                          }`}
                        >
                          <span className="flex items-center">
                            <UserX className="w-5 h-5 mr-2" />
                            <span>Kişiyi Engelle</span>
                          </span>
                          <ChevronRight className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Footer */}
              <div className={`px-6 py-4 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'} flex justify-end`}>
                <button 
                  onClick={() => setShowChatInfo(false)}
                  className={`px-4 py-2 rounded-lg ${
                        isDark 
                      ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                >
                  Kapat
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Grup Adı Düzenleme Modalı */}
      {activeChat && isEditingGroupName && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className={`bg-white ${isDark ? 'dark:bg-gray-800 dark:text-white' : ''} rounded-lg shadow-lg max-w-md w-full p-4`}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Grup Adını Düzenle</h3>
              <button
                onClick={() => setIsEditingGroupName(false)}
                className={`p-1 rounded-full ${
                  isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-600'
                }`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-4">
              <input
                type="text"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                placeholder="Grup adı girin"
                className={`w-full p-2 border rounded-md ${
                  isDark ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'
                }`}
                maxLength={50}
              />
              <div className="text-xs text-right mt-1 text-gray-500">
                {newGroupName.length}/50
              </div>
            </div>
            
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setIsEditingGroupName(false)}
                className={`px-4 py-2 rounded-md ${
                  isDark ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-800'
                }`}
              >
                İptal
              </button>
              <button
                onClick={() => updateGroupName(activeChat, newGroupName)}
                disabled={!newGroupName.trim() || newGroupName.trim() === activeChat?.title}
                className={`px-4 py-2 rounded-md bg-blue-500 text-white ${
                  (!newGroupName.trim() || newGroupName.trim() === activeChat?.title) 
                  ? 'opacity-50 cursor-not-allowed' 
                  : 'hover:bg-blue-600'
                }`}
              >
                Kaydet
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Grup Açıklaması Düzenleme Modalı */}
      {activeChat && isEditingGroupDesc && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className={`bg-white ${isDark ? 'dark:bg-gray-800 dark:text-white' : ''} rounded-lg shadow-lg max-w-md w-full p-4`}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Grup Açıklamasını Düzenle</h3>
              <button
                onClick={() => setIsEditingGroupDesc(false)}
                className={`p-1 rounded-full ${
                  isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-600'
                }`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-4">
              <textarea
                value={newGroupDesc}
                onChange={(e) => setNewGroupDesc(e.target.value)}
                placeholder="Grup açıklaması girin"
                className={`w-full p-2 border rounded-md ${
                  isDark ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'
                }`}
                rows={4}
                maxLength={500}
              />
              <div className="text-xs text-right mt-1 text-gray-500">
                {newGroupDesc.length}/500
              </div>
            </div>
            
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setIsEditingGroupDesc(false)}
                className={`px-4 py-2 rounded-md ${
                  isDark ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-800'
                }`}
              >
                İptal
              </button>
              <button
                onClick={() => handleUpdateGroupDescription(newGroupDesc)}
                disabled={newGroupDesc.trim() === activeChat?.description}
                className={`px-4 py-2 rounded-md bg-blue-500 text-white ${
                  (newGroupDesc.trim() === activeChat?.description) 
                  ? 'opacity-50 cursor-not-allowed' 
                  : 'hover:bg-blue-600'
                }`}
              >
                Kaydet
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Üye Ekleme Modalı */}
      <AnimatePresence>
        {showAddMemberModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className={`w-full max-w-md rounded-xl shadow-xl ${
                isDark ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-800'
              } overflow-hidden max-h-[90vh] flex flex-col`}
            >
              <div className={`px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'} flex justify-between items-center`}>
                <h3 className="text-lg font-semibold">Üye Ekle</h3>
                <button 
                  onClick={() => {
                    setShowAddMemberModal(false);
                    setSelectedNewMembers([]);
                  }}
                  className={`p-2 rounded-full ${
                    isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-600'
                  }`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-4 border-b border-gray-200">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    placeholder="Kullanıcı ara..."
                    className={`w-full pl-10 pr-4 py-2 rounded-lg ${
                      isDark 
                        ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                        : 'bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500'
                    } border focus:outline-none focus:ring-1 focus:ring-blue-500`}
                    onChange={(e) => {
                      const query = e.target.value.toLowerCase();
                      if (!query) {
                        setPotentialMembers(potentialMembers.map(user => ({ ...user, filtered: false })));
                      } else {
                        setPotentialMembers(potentialMembers.map(user => ({
                          ...user,
                          filtered: !user.name.toLowerCase().includes(query) && 
                                    !user.email.toLowerCase().includes(query)
                        })));
                      }
                    }}
                  />
                </div>
              </div>
              
              <div className="overflow-y-auto flex-1 p-2">
                <div className="space-y-1">
                  {potentialMembers.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">
                      Kullanıcı bulunamadı
                    </div>
                  ) : (
                    potentialMembers
                      .filter(user => !user.filtered)
                      .map((user) => (
                        <div 
                          key={user.id}
                          onClick={() => {
                            const isSelected = selectedNewMembers.some(m => m.id === user.id);
                            if (isSelected) {
                              setSelectedNewMembers(selectedNewMembers.filter(m => m.id !== user.id));
                            } else {
                              setSelectedNewMembers([...selectedNewMembers, user]);
                            }
                          }}
                          className={`flex items-center p-3 rounded-lg cursor-pointer ${
                            selectedNewMembers.some(m => m.id === user.id)
                              ? (isDark ? 'bg-gray-700' : 'bg-blue-50')
                              : (isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100')
                          }`}
                        >
                          <div className="flex-shrink-0 mr-3">
                            {user.avatar ? (
                              <img src={user.avatar} alt={user.name} className="w-10 h-10 rounded-full" />
                            ) : (
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                isDark ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-700'
                              }`}>
                                {getInitials(user.name)}
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'} truncate`}>
                              {user.name}
                            </p>
                            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'} truncate`}>
                              {user.email}
                            </p>
                          </div>
                          <div className="flex-shrink-0">
                            <div className={`w-5 h-5 rounded-full border ${
                              selectedNewMembers.some(m => m.id === user.id)
                                ? 'bg-blue-500 border-blue-500'
                                : (isDark ? 'border-gray-500' : 'border-gray-300')
                            } flex items-center justify-center`}>
                              {selectedNewMembers.some(m => m.id === user.id) && (
                                <Check className="w-3 h-3 text-white" />
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                  )}
                </div>
              </div>
              
              <div className={`px-6 py-4 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'} flex justify-between items-center`}>
                <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {selectedNewMembers.length} kişi seçildi
                </div>
                <div className="flex gap-2">
                  <button 
                    onClick={() => {
                      setShowAddMemberModal(false);
                      setSelectedNewMembers([]);
                    }}
                    className={`px-4 py-2 rounded-lg ${
                      isDark 
                        ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' 
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                    }`}
                  >
                    İptal
                  </button>
                  <button 
                    onClick={() => addMembersToGroup(activeChat)}
                    className={`px-4 py-2 rounded-lg ${
                      selectedNewMembers.length > 0 
                        ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                        : 'bg-blue-400 text-white cursor-not-allowed'
                    }`}
                    disabled={selectedNewMembers.length === 0}
                  >
                    {selectedNewMembers.length === 0 ? 'Üye Seç' : 'Ekle'}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Sohbette Arama Modalı */}
      <AnimatePresence>
        {showSearchChatModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className={`w-full max-w-md rounded-xl shadow-xl ${
                isDark ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-800'
              } overflow-hidden max-h-[90vh] flex flex-col`}
            >
              <div className={`px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'} flex justify-between items-center`}>
                <h3 className="text-lg font-semibold">Sohbette Ara</h3>
                <button 
                  onClick={() => setShowSearchChatModal(false)}
                  className={`p-2 rounded-full ${
                    isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-600'
                  }`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-4 border-b border-gray-200">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    value={chatSearchQuery}
                    onChange={(e) => setChatSearchQuery(e.target.value)}
                    placeholder="Mesajlarda ara..."
                    className={`w-full pl-10 pr-4 py-2 rounded-lg ${
                      isDark 
                        ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                        : 'bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500'
                    } border focus:outline-none focus:ring-1 focus:ring-blue-500`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        searchInChat(chatSearchQuery);
                      }
                    }}
                  />
                </div>
                <button
                  onClick={() => searchInChat(chatSearchQuery)}
                  className={`mt-2 w-full px-4 py-2 rounded-lg ${
                    isDark 
                      ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }`}
                >
                  Ara
                </button>
              </div>
              
              <div className="overflow-y-auto flex-1 p-2">
                {chatSearchResults.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    {chatSearchQuery.trim() ? 'Kişi bulunamadı' : 'Arama yapmak için metin girin'}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {chatSearchResults.map((msg) => (
                      <div 
                        key={msg.message_id}
                        className={`p-3 rounded-lg ${
                          isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
                        } cursor-pointer`}
                        onClick={() => {
                          setShowSearchChatModal(false);
                          // Mesaja scroll yapma işlevi burada eklenir
                        }}
                      >
                        <div className="flex justify-between items-center mb-1">
                          <span className={`text-xs font-medium ${
                            isDark ? 'text-gray-300' : 'text-gray-700'
                          }`}>
                            {msg.sender_name || 'Anonim'}
                          </span>
                          <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            {new Date(msg.created_at).toLocaleString()}
                          </span>
                        </div>
                        <p className={`text-sm ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                          {msg.message_text}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className={`px-6 py-4 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'} flex justify-end`}>
                <button 
                  onClick={() => setShowSearchChatModal(false)}
                  className={`px-4 py-2 rounded-lg ${
                    isDark 
                      ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                >
                  Kapat
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Engellenen Kullanıcılar Modalı */}
      <AnimatePresence>
        {showBlockedUsersModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className={`w-full max-w-md rounded-xl shadow-xl ${
                isDark ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-800'
              } overflow-hidden max-h-[90vh] flex flex-col`}
            >
              <div className={`px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'} flex justify-between items-center`}>
                <h3 className="text-lg font-semibold">Engellenen Kullanıcılar</h3>
                <button 
                  onClick={() => setShowBlockedUsersModal(false)}
                  className={`p-2 rounded-full ${
                    isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-600'
                  }`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="overflow-y-auto flex-1 p-4">
                {blockedUsers.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center mx-auto mb-4">
                      <Shield className={`w-8 h-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                    </div>
                    <p className={`text-base font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                      Engellenen kullanıcı yok
                    </p>
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      Engellemek istediğiniz kullanıcının profil sayfasından engelleyebilirsiniz.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {users
                      .filter(user => blockedUsers.includes(user.id))
                      .map((user) => (
                        <div 
                          key={user.id}
                          className={`flex items-center p-3 rounded-lg ${
                            isDark ? 'bg-gray-700' : 'bg-gray-100'
                          }`}
                        >
                          <div className="flex-shrink-0 mr-3">
                            {user.avatar ? (
                              <img src={user.avatar} alt={user.name} className="w-10 h-10 rounded-full" />
                            ) : (
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                isDark ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-700'
                              }`}>
                                {getInitials(user.name)}
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'} truncate`}>
                              {user.name}
                            </p>
                            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'} truncate`}>
                              {user.email}
                            </p>
                          </div>
                          <button
                            onClick={() => toggleBlockUser(user.id, user.name)}
                            className={`px-3 py-1 rounded text-xs font-medium ${
                              isDark ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                          >
                            Engeli Kaldır
                          </button>
                        </div>
                      ))}
                  </div>
                )}
              </div>
              
              <div className={`px-6 py-4 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'} flex justify-end`}>
                <button 
                  onClick={() => setShowBlockedUsersModal(false)}
                  className={`px-4 py-2 rounded-lg ${
                    isDark 
                      ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                >
                  Kapat
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Arşivlenmiş Sohbetler Modalı */}
      <AnimatePresence>
        {showArchivedChatsModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className={`w-full max-w-md rounded-xl shadow-xl ${
                isDark ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-800'
              } overflow-hidden max-h-[90vh] flex flex-col`}
            >
              <div className={`px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'} flex justify-between items-center`}>
                <h3 className="text-lg font-semibold">Arşivlenmiş Sohbetler</h3>
                <button 
                  onClick={() => setShowArchivedChatsModal(false)}
                  className={`p-2 rounded-full ${
                    isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-600'
                  }`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="overflow-y-auto flex-1 p-4">
                {archivedChats.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center mx-auto mb-4">
                      <Archive className={`w-8 h-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                    </div>
                    <p className={`text-base font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                      Arşivlenen sohbet yok
                    </p>
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      Arşivlemek istediğiniz sohbetin ayarlar menüsünden arşivleyebilirsiniz.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {conversations
                      .filter(chat => archivedChats.includes(chat.conversation_id))
                      .map((chat) => (
                        <div 
                          key={chat.conversation_id}
                          className={`flex items-center p-3 rounded-lg ${
                            isDark ? 'bg-gray-700' : 'bg-gray-100'
                          }`}
                        >
                          <div className="flex-shrink-0 mr-3">
                            {chat.is_group ? (
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                isDark ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-700'
                              }`}>
                                <Users className="w-5 h-5" />
                              </div>
                            ) : chat.otherUser?.avatar ? (
                              <img src={chat.otherUser.avatar} alt={chat.title} className="w-10 h-10 rounded-full" />
                            ) : (
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                isDark ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-700'
                              }`}>
                                {getInitials(chat.title)}
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'} truncate`}>
                              {chat.title}
                            </p>
                            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'} truncate`}>
                              {chat.last_message || 'Henüz mesaj yok'}
                            </p>
                          </div>
                          <button
                            onClick={() => toggleArchiveChat(chat.conversation_id, chat.title)}
                            className={`px-3 py-1 rounded text-xs font-medium ${
                              isDark ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                          >
                            Arşivden Çıkar
                          </button>
                        </div>
                      ))}
                  </div>
                )}
              </div>
              
              <div className={`px-6 py-4 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'} flex justify-end`}>
                <button 
                  onClick={() => setShowArchivedChatsModal(false)}
                  className={`px-4 py-2 rounded-lg ${
                    isDark 
                      ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                >
                  Kapat
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
{/* Grup Fotoğrafı Değiştirme Modalı */}
{activeChat && isChangingGroupPhoto && (
  <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
    <div className={`bg-white ${isDark ? 'dark:bg-gray-800 dark:text-white' : ''} rounded-lg shadow-lg max-w-md w-full p-4`}>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Grup Fotoğrafını Değiştir</h3>
        <button
          onClick={() => {
            setIsChangingGroupPhoto(false);
            setGroupPhoto(null);
            setGroupPhotoPreview(null);
          }}
          className={`p-1 rounded-full ${
            isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-600'
          }`}
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      
      <div className="flex flex-col items-center justify-center mb-4">
        <div className={`w-32 h-32 rounded-full overflow-hidden mb-4 ${
          isDark ? 'bg-gray-700' : 'bg-gray-200'
        } flex items-center justify-center`}>
{groupPhotoPreview ? (
  <img 
    src={groupPhotoPreview} 
    alt="Grup fotoğrafı önizleme" 
    className="w-full h-full object-cover"
  />
) : currentChat?.group_photo_url ? (
  <img 
    src={currentChat.group_photo_url} 
    alt="Mevcut grup fotoğrafı" 
    className="w-full h-full object-cover"
    onError={(e) => {
      e.target.onerror = null;
      e.target.src = "";
      e.target.parentElement.innerHTML = `<div class="w-full h-full flex items-center justify-center bg-gray-700"><Camera class="w-12 h-12 text-gray-400" /></div>`;
    }}
  />
) : (
  <Camera className="w-12 h-12 text-gray-400" />
)}
        </div>
        
        <div className="flex flex-wrap gap-2 justify-center mb-2">
          <input
            type="file"
            id="group-photo-input"
            className="hidden"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) {
                setGroupPhoto(file);
                const reader = new FileReader();
                reader.onloadend = () => {
                  setGroupPhotoPreview(reader.result);
                };
                reader.readAsDataURL(file);
              }
            }}
          />
          
          <label
            htmlFor="group-photo-input"
            className={`px-4 py-2 rounded-md cursor-pointer ${
              isDark ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
            }`}
          >
            Fotoğraf Seç
          </label>
          
          {(currentChat?.group_photo_url || groupPhotoPreview) && (
  <button
    onClick={removeGroupPhoto}
    className={`px-4 py-2 rounded-md ${
      isDark ? 'bg-red-900/70 hover:bg-red-800 text-red-100' : 'bg-red-100 hover:bg-red-200 text-red-800'
    }`}
  >
    Fotoğrafı Kaldır
  </button>
)}
        </div>
        
        {currentChat?.group_photo_url && !groupPhotoPreview && (
  <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
    Mevcut fotoğrafı görüntülüyorsunuz
  </p>
)}
      </div>
      
      <div className="flex justify-end space-x-2">
        <button
          onClick={() => {
            setIsChangingGroupPhoto(false);
            setGroupPhoto(null);
            setGroupPhotoPreview(null);
          }}
          className={`px-4 py-2 rounded-md ${
            isDark ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-800'
          }`}
        >
          İptal
        </button>
        <button
  onClick={() => updateGroupPhoto(currentChat?.id, groupPhoto)}
  disabled={!groupPhoto}
  className={`px-4 py-2 rounded-md bg-blue-500 text-white ${
    !groupPhoto ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-600'
  }`}
>
  Yükle
</button>
      </div>
    </div>
  </div>
)}
      
      {/* Üyeleri Yönetme Modalı */}
      {activeChat && showManageMembersModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className={`bg-white ${isDark ? 'dark:bg-gray-800 dark:text-white' : ''} rounded-lg shadow-lg max-w-md w-full p-4`}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Üyeleri Yönet</h3>
              <button
                onClick={() => setShowManageMembersModal(false)}
                className={`p-1 rounded-full ${
                  isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-600'
                }`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <p className={`text-sm mb-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              Üye rollerini değiştirmek veya üyeleri gruptan çıkarmak için yönetici olmanız gerekir.
            </p>
            
            <div className="mb-4">
              <input
                type="text"
                placeholder="Üye ara..."
                className={`w-full p-2 border rounded-md ${
                  isDark ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'
                }`}
              />
            </div>
            
            <div className={`rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-100'} p-2 mb-4 max-h-60 overflow-y-auto`}>
              {activeChat?.members?.map((member, index) => {
                // Mevcut kullanıcının yönetici olup olmadığını kontrol et
                const currentUserMember = activeChat.members.find(m => m.id === currentUser.id);
                const isCurrentUserAdmin = currentUserMember && currentUserMember.role === 'admin';

  return (
                  <div 
                    key={index} 
                    className={`flex items-center justify-between p-2 ${
                      isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-200'
                    } rounded-md mb-1`}
                  >
                    <div className="flex items-center">
                      {member.avatar ? (
                        <img src={member.avatar} alt={member.name} className="w-8 h-8 rounded-full mr-2" />
                      ) : (
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-2 ${
                          isDark ? 'bg-gray-600 text-gray-300' : 'bg-gray-300 text-gray-700'
                        }`}>
                          {getInitials(member.name)}
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-medium">{member.name}</p>
                        <div className="flex items-center">
                          {member.role === 'admin' && (
                            <span className={`text-xs px-2 py-0.5 rounded-full mr-1 ${
                              isDark ? 'bg-blue-900 text-blue-200' : 'bg-blue-100 text-blue-800'
                            }`}>Yönetici</span>
                          )}
                          {member.id === currentUser.id && (
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              isDark ? 'bg-green-900 text-green-200' : 'bg-green-100 text-green-800'
                            }`}>Sen</span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {isCurrentUserAdmin && member.id !== currentUser.id && (
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => {
                            const newRole = member.role === 'admin' ? 'member' : 'admin';
                            updateMemberRole(activeChat.conversation_id, member.id, member.name, newRole);
                          }}
                          className={`p-1 rounded-md ${
                            isDark 
                              ? 'bg-blue-900/60 hover:bg-blue-900 text-blue-200' 
                              : 'bg-blue-100 hover:bg-blue-200 text-blue-700'
                          }`}
                          title={member.role === 'admin' ? 'Yöneticiliği Al' : 'Yönetici Yap'}
                        >
                          {member.role === 'admin' ? <UserMinus className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
                        </button>
                        
                        <button
                          onClick={() => {
                            if (confirm(`${member.name} adlı üyeyi gruptan çıkarmak istediğinize emin misiniz?`)) {
                              removeMemberFromGroup(activeChat.conversation_id, member.id, member.name);
                            }
                          }}
                          className={`p-1 rounded-md ${
                            isDark 
                              ? 'bg-red-900/60 hover:bg-red-900 text-red-200' 
                              : 'bg-red-100 hover:bg-red-200 text-red-700'
                          }`}
                          title="Gruptan Çıkar"
                        >
                          <UserX className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            
            <div className="flex justify-end">
              <button
                onClick={() => setShowManageMembersModal(false)}
                className={`px-4 py-2 rounded-md ${
                  isDark ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-800'
                }`}
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Chat; 